import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiCheck, FiChevronDown, FiChevronUp, FiChevronRight, FiUpload, FiStar, FiCheckCircle, FiXCircle, FiAward, FiClock } from 'react-icons/fi';
import { formsAPI, responsesAPI, certificatesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import CalculationField from '../components/CalculationField';
import MatrixField from '../components/MatrixField';
import TableField from '../components/TableField';
import MapLocationField from '../components/MapLocationField';
import { calculateQuestionnaireScore, isQuestionnaire } from '../utils/questionnaireScoring';
import { getAnalyticsData, calculateDuration } from '../utils/analytics';
import { isSingleAgreementCheckbox, getAgreementText, normalizeFormStructure, AgreementCheckboxField } from '../utils/checkboxField';

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 40px 20px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px 12px;
    max-width: 100%;
    overflow-x: hidden; /* Prevent horizontal overflow on mobile */
  }
  
  @media (max-width: 480px) {
    padding: 16px 8px;
    overflow-x: hidden; /* Prevent horizontal overflow on small screens */
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  margin-bottom: 32px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #e5e7eb;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 24px 16px;
    border-radius: 12px;
    overflow-x: hidden; /* Prevent horizontal overflow on mobile */
  }
  
  @media (max-width: 480px) {
    padding: 20px 12px;
    border-radius: 8px;
    margin: 0 -4px;
    overflow-x: hidden; /* Prevent horizontal overflow on small screens */
  }
`;

const FormTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 16px;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 10px;
    line-height: 1.4;
  }
`;

const FormDescription = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 40px;
  text-align: center;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const FormField = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fieldWidth', 'fieldType'].includes(prop),
})`
  margin-bottom: 24px;
  
  /* Grid column span based on fieldWidth */
  grid-column: ${props => {
    if (props.fieldType === 'section' || props.fieldType === 'heading' || props.fieldType === 'subheading') {
      return 'span 12'; // Always full width for sections/headings
    }
    if (props.fieldWidth === 'full') return 'span 12';
    if (props.fieldWidth === 'half') return 'span 6';
    if (props.fieldWidth === 'third') return 'span 4';
    if (props.fieldWidth === 'two-thirds') return 'span 8';
    if (props.fieldWidth === 'quarter') return 'span 3';
    return 'span 12'; // default to full width
  }};
  
  @media (max-width: 768px) {
    grid-column: span 12 !important; /* Force full width on mobile */
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
    overflow-x: hidden; /* Prevent horizontal overflow on mobile */
  }
`;

const FieldLabel = styled.label.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic'].includes(prop),
})`
  display: block;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  font-size: ${props => props.fontSize || '14px'};
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : '500'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  width: 100%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  line-height: 1.5;
  
  ${props => props.required && `
    &::after {
      content: ' *';
      color: #ef4444;
    }
  `}
  
  @media (max-width: 768px) {
    font-size: ${props => {
      const baseSize = parseInt(props.fontSize) || 14;
      return Math.max(14, baseSize - 1) + 'px';
    }};
    margin-bottom: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: ${props => {
      const baseSize = parseInt(props.fontSize) || 14;
      return Math.max(13, baseSize - 2) + 'px';
    }};
    margin-bottom: 8px;
    line-height: 1.4;
  }
`;

const FieldInput = styled.input.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px 12px ${props => {
    const paddingLeft = parseInt(props.inputPaddingLeft) || 16;
    console.log('FormView FieldInput padding props:', props.inputPaddingLeft, 'parsed:', paddingLeft);
    return paddingLeft;
  }}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  overflow: hidden;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:invalid {
    border-color: #ef4444;
  }
  
  /* Date input specific styling for mobile */
  &[type="date"] {
    position: relative;
    
    /* Hide native date picker icon on some browsers to prevent overflow */
    &::-webkit-calendar-picker-indicator {
      position: absolute;
      right: 8px;
      cursor: pointer;
    }
    
    /* Ensure date input fits on mobile */
    @media (max-width: 768px) {
      padding-right: 40px; /* Make room for calendar icon */
      min-width: 0; /* Allow shrinking */
    }
    
    @media (max-width: 480px) {
      padding-right: 36px;
      font-size: 16px; /* Prevent zoom on iOS */
    }
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 48px; /* Better touch target */
    min-width: 0; /* Allow flex items to shrink */
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    min-width: 0; /* Allow flex items to shrink */
  }
`;

const FieldTextarea = styled.textarea.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  max-width: 100%;
  padding: 12px 16px 12px ${props => {
    const paddingLeft = parseInt(props.inputPaddingLeft) || 16;
    console.log('FormView FieldInput padding props:', props.inputPaddingLeft, 'parsed:', paddingLeft);
    return paddingLeft;
  }}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  min-height: 120px;
  resize: vertical;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-wrap: break-word;
  overflow-x: hidden;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 100px;
    min-width: 0; /* Allow flex items to shrink */
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    min-height: 80px;
    font-size: 16px; /* Prevent zoom on iOS */
  }
`;

const FieldSelect = styled.select.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  max-width: 100%;
  padding: 12px 40px 12px ${props => {
    const paddingLeft = parseInt(props.inputPaddingLeft) || 16;
    console.log('FormView FieldInput padding props:', props.inputPaddingLeft, 'parsed:', paddingLeft);
    return paddingLeft;
  }}px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  background: white;
  cursor: pointer;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23374151' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 14px 40px 14px 16px;
    font-size: 16px; /* Prevent zoom on iOS */
    min-height: 48px; /* Better touch target */
    min-width: 0; /* Allow flex items to shrink */
    background-position: right 12px center;
  }
  
  @media (max-width: 480px) {
    padding: 12px 36px 12px 14px;
    font-size: 16px; /* Prevent zoom on iOS */
    background-position: right 10px center;
  }
`;

const RadioGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    gap: 14px; /* Better spacing on mobile */
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    padding-left: ${props => Math.max(0, (props.inputPaddingLeft || 0) - 4)}px; /* Reduce padding on small screens */
  }
`;

const RadioItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px; /* Better touch target on mobile */
  padding: 4px 0;
  
  @media (max-width: 768px) {
    gap: 14px;
    min-height: 48px; /* Larger touch target */
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    min-height: 44px;
  }
  
  span {
    flex: 1;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 16px; /* Prevent zoom on iOS */
    
    @media (max-width: 480px) {
      font-size: 15px;
    }
  }
`;

const RadioInput = styled.input`
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const CheckboxGroup = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft', 'checkboxLayout'].includes(prop),
})`
  display: flex;
  flex-direction: ${props => props.checkboxLayout === 'horizontal' ? 'row' : 'column'};
  flex-wrap: ${props => props.checkboxLayout === 'horizontal' ? 'wrap' : 'nowrap'};
  gap: 12px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    gap: 14px;
    ${props => props.checkboxLayout === 'horizontal' && `
      gap: 10px;
    `}
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    padding-left: ${props => Math.max(0, (props.inputPaddingLeft || 0) - 4)}px;
    ${props => props.checkboxLayout === 'horizontal' && `
      gap: 8px;
    `}
  }
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 44px; /* Better touch target on mobile */
  padding: 4px 0;
  
  @media (max-width: 768px) {
    gap: 14px;
    min-height: 48px; /* Larger touch target */
  }
  
  @media (max-width: 480px) {
    gap: 12px;
    min-height: 44px;
  }
  
  span {
    flex: 1;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 16px; /* Prevent zoom on iOS */
    
    @media (max-width: 480px) {
      font-size: 15px;
    }
  }
`;

const CheckboxInput = styled.input`
  width: 20px;
  height: 20px;
  margin: 0;
  flex-shrink: 0;
  cursor: pointer;
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
  }
  
  @media (max-width: 480px) {
    width: 20px;
    height: 20px;
  }
`;

const FieldHeading = styled.h3`
  font-size: ${props => {
    switch (props.level) {
      case 1: return '28px';
      case 2: return '24px';
      case 3: return '20px';
      case 4: return '18px';
      case 5: return '16px';
      case 6: return '14px';
      default: return '20px';
    }
  }};
  font-weight: 600;
  color: #1f2937;
  margin: 32px 0 16px 0;
  padding: 0;
  border: none;
  background: none;
  width: 100%;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;

  &:hover {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 18px 16px;
    font-size: 16px;
    min-height: 52px; /* Better touch target */
  }
  
  @media (max-width: 480px) {
    padding: 16px 12px;
    font-size: 15px;
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #059669;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #d1fae5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 32px;
  color: #059669;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: #6b7280;
`;

const ErrorMessage = styled.div`
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 6px;
  margin-bottom: 24px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  
  img {
    max-width: 200px;
    max-height: 100px;
    object-fit: contain;
    border-radius: 8px;
  }
`;

const DefaultLogo = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  font-weight: bold;
  color: white;
  text-transform: uppercase;
`;

const ContactToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #0277bd;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 12px;
  transition: color 0.2s ease;

  &:hover {
    color: #0369a1;
  }

  svg {
    transition: transform 0.2s ease;
  }
`;

const ContactFieldsContainer = styled.div`
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
  max-height: ${props => props.collapsed ? '0' : '1000px'};
  opacity: ${props => props.collapsed ? '0' : '1'};
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const FileUploadContainer = styled.div`
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  background: #f9fafb;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  cursor: pointer;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;

  &:hover {
    border-color: #3b82f6;
    background: #eff6ff;
  }

  &.drag-over {
    border-color: #3b82f6;
    background: #eff6ff;
  }
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 12px;
    border-radius: 6px;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #6b7280;
`;

const FileUploadIcon = styled.div`
  font-size: 32px;
  color: #9ca3af;
`;

const FileUploadButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: #2563eb;
  }
`;

const FileList = styled.div`
  margin-top: 12px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  gap: 8px;
  
  @media (max-width: 480px) {
    padding: 8px 10px;
    flex-wrap: wrap;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #374151;
`;

const RemoveFileButton = styled.button`
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: #dc2626;
  }
`;

const RatingContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
  width: 100%;
  max-width: 100%;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
    padding-left: ${props => Math.max(0, (props.inputPaddingLeft || 0) - 4)}px;
  }
`;

const StarContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  
  @media (max-width: 768px) {
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    gap: 4px;
  }
`;

const StarButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${props => props.filled ? '#fbbf24' : '#d1d5db'};
  font-size: 24px;
  transition: color 0.2s ease;
  min-width: 44px; /* Better touch target */
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${props => props.filled ? '#f59e0b' : '#9ca3af'};
  }
  
  @media (max-width: 768px) {
    font-size: 28px;
    min-width: 48px;
    min-height: 48px;
    padding: 10px;
  }
  
  @media (max-width: 480px) {
    font-size: 24px;
    min-width: 44px;
    min-height: 44px;
    padding: 8px;
  }
`;

const RatingLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  
  @media (max-width: 480px) {
    font-size: 11px;
  }
`;

const SliderContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['fontFamily', 'bold', 'italic', 'inputPaddingLeft'].includes(prop),
})`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  font-family: ${props => props.fontFamily || 'inherit'};
  font-weight: ${props => props.bold ? '700' : 'normal'};
  font-style: ${props => props.italic ? 'italic' : 'normal'};
  padding-left: ${props => props.inputPaddingLeft || 0}px;
  
  @media (max-width: 768px) {
    padding-left: ${props => Math.max(0, (props.inputPaddingLeft || 0) - 4)}px;
  }
  
  @media (max-width: 480px) {
    padding-left: ${props => Math.max(0, (props.inputPaddingLeft || 0) - 8)}px;
  }
`;

const SliderInput = styled.input`
  width: 100%;
  max-width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #e5e7eb;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #3b82f6;
    cursor: pointer;
    border: none;
    transition: all 0.2s ease;
  }
  
  &:hover::-webkit-slider-thumb {
    background: #2563eb;
    transform: scale(1.1);
  }
  
  &:hover::-moz-range-thumb {
    background: #2563eb;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    height: 8px;
    min-height: 48px; /* Better touch target */
    
    &::-webkit-slider-thumb {
      width: 24px;
      height: 24px;
    }
    
    &::-moz-range-thumb {
      width: 24px;
      height: 24px;
    }
  }
  
  @media (max-width: 480px) {
    height: 8px;
    min-height: 44px;
    
    &::-webkit-slider-thumb {
      width: 22px;
      height: 22px;
    }
    
    &::-moz-range-thumb {
      width: 22px;
      height: 22px;
    }
  }
`;

const SliderValue = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
  text-align: center;
  
  @media (max-width: 480px) {
    font-size: 15px;
    margin-top: 10px;
  }
`;

const TimerBanner = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: -8px -8px 20px -8px;
  padding: 12px;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
  color: white;
  background: ${props => props.$urgent ? '#dc2626' : '#1f2937'};
  transition: background 0.3s ease;
`;

const ScoreCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 32px;
  margin: 24px 0;
  color: white;
  text-align: center;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
`;

const ScoreTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const ScoreValue = styled.div`
  font-size: 48px;
  font-weight: 800;
  margin: 16px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const ScoreDetails = styled.div`
  font-size: 18px;
  margin: 12px 0;
  opacity: 0.95;
`;

const ScoreGrade = styled.div`
  display: inline-block;
  padding: 8px 24px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 24px;
  font-size: 20px;
  font-weight: 700;
  margin-top: 16px;
`;

const QuestionResults = styled.div`
  margin-top: 32px;
  padding: 24px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const QuestionResult = styled.div`
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  border-left: 4px solid ${props => props.isCorrect ? '#10b981' : props.requiresGrading ? '#f59e0b' : '#ef4444'};
  background: ${props => props.isCorrect ? '#f0fdf4' : props.requiresGrading ? '#fffbeb' : '#fef2f2'};
`;

const QuestionResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const QuestionText = styled.div`
  font-weight: 600;
  color: #1f2937;
  flex: 1;
`;

const QuestionScore = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.isCorrect ? '#059669' : props.requiresGrading ? '#d97706' : '#dc2626'};
`;

const AnswerDetails = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
  
  div {
    margin: 4px 0;
  }
`;

const QuestionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 2px solid #e5e7eb;
  transition: all 0.2s ease;

  &:hover {
    border-color: #9ca3af;
  }
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    margin-bottom: 20px;
    border-radius: 8px;
  }
  
  @media (max-width: 480px) {
    padding: 16px 12px;
    margin-bottom: 16px;
    border-radius: 6px;
  }
`;

const QuestionNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 12px;
`;

const QuestionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 16px;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 14px;
    line-height: 1.4;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 12px;
  }
`;

const QuestionOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionItem = styled.label`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${props => props.checked ? '#f0f9ff' : '#f9fafb'};
  border: 2px solid ${props => props.checked ? '#3b82f6' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px; /* Better touch target */

  &:hover {
    background: ${props => props.checked ? '#e0f2fe' : '#f3f4f6'};
    border-color: ${props => props.checked ? '#2563eb' : '#d1d5db'};
  }
  
  @media (max-width: 768px) {
    padding: 14px 16px;
    min-height: 52px;
    border-radius: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 12px 14px;
    min-height: 48px;
  }
`;

const OptionInput = styled.input`
  margin-right: 12px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    margin-right: 14px;
  }
`;

const OptionText = styled.span`
  font-size: 15px;
  color: #374151;
  font-weight: ${props => props.checked ? '600' : '400'};
`;

const QuestionPointsBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  margin-left: 8px;
`;


function FormView({ formId }) {
  const { id } = useParams();
  const actualFormId = formId || id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [contactBlockCollapsed, setContactBlockCollapsed] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [scoreData, setScoreData] = useState(null);
  const [certificateData, setCertificateData] = useState(null);
  const [certificateGenerating, setCertificateGenerating] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [repeatableInstances, setRepeatableInstances] = useState({}); // Track instances for each repeatable field
  const [repeatableContinueStates, setRepeatableContinueStates] = useState({}); // Track continue state for each instance
  const [optionBookingCounts, setOptionBookingCounts] = useState({}); // fieldId -> { [optionLabel]: number }
  const [sessionId] = useState(() => {
    // Generate or retrieve session ID for tracking
    let sessionId = localStorage.getItem(`form_session_${id}`);
    if (!sessionId) {
      sessionId = Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem(`form_session_${id}`, sessionId);
    }
    return sessionId;
  });

  // Analytics tracking state
  const [analyticsData, setAnalyticsData] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(new Date().toISOString());
  const [sessionEndTime, setSessionEndTime] = useState(null);

  // Quiz timer state (only used when form.form_structure.enableTimer is true).
  // The countdown is derived from a fixed end-timestamp (not decremented per tick) so it
  // stays correct even if the browser throttles setInterval in a backgrounded/inactive tab.
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(null);
  const quizEndTimeRef = useRef(null);
  const autoSubmitTriggeredRef = useRef(false);

  // Set the deadline once a timed quiz has loaded
  useEffect(() => {
    if (form && isQuestionnaire(form) && form.form_structure?.enableTimer) {
      const limitMinutes = form.form_structure.timeLimitMinutes || 10;
      quizEndTimeRef.current = Date.now() + limitMinutes * 60 * 1000;
      setTimeRemainingSeconds(limitMinutes * 60);
    }
  }, [form?.id]);

  // Recompute remaining time from the deadline on every tick
  useEffect(() => {
    const timerActive = form && isQuestionnaire(form) && form.form_structure?.enableTimer && !submitted;
    if (!timerActive) return undefined;

    const tick = () => {
      if (!quizEndTimeRef.current) return;
      const remaining = Math.max(0, Math.round((quizEndTimeRef.current - Date.now()) / 1000));
      setTimeRemainingSeconds(remaining);
    };

    tick();
    const interval = setInterval(tick, 1000);

    // Browsers throttle setInterval in backgrounded tabs, so the countdown can lag while
    // the user is on another tab. Force an immediate recalculation the moment they come
    // back, so the display (and auto-submit) catches up right away instead of staying stale.
    document.addEventListener('visibilitychange', tick);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [form?.id, form?.form_structure?.enableTimer, submitted]);

  // Auto-submit whatever's answered once time runs out
  useEffect(() => {
    if (timeRemainingSeconds === 0 && !submitted && !autoSubmitTriggeredRef.current) {
      autoSubmitTriggeredRef.current = true;
      toast.info("Time's up! Submitting your answers...");
      handleSubmit({ preventDefault: () => {} }, { skipValidation: true });
    }
  }, [timeRemainingSeconds]);

  const formatTimerDisplay = (totalSeconds) => {
    if (totalSeconds === null || totalSeconds === undefined) return '';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };


  // Collect analytics data
  const collectAnalyticsData = async () => {
    try {
      const data = await getAnalyticsData();
      setAnalyticsData(data);
      console.log('Analytics data collected:', data);
    } catch (error) {
      console.error('Failed to collect analytics data:', error);
    }
  };

  const trackFormView = async () => {
    try {
      await responsesAPI.trackView(actualFormId, sessionId);
      console.log('Form view tracked successfully');
    } catch (error) {
      console.error('Failed to track form view:', error);
      // Don't show error to user as this is background tracking
    }
  };


  const loadForm = async () => {
    try {
      setLoading(true);
      
      // All forms are now publicly accessible
      const response = await formsAPI.getShared(actualFormId);
      
      const formData = response.data.data;
      
      // Check if form is manually deactivated
      if (!formData.is_active) {
        toast.error('This form is currently inactive and not accepting responses.');
        navigate('/');
        return;
      }
      
      console.log('FormView: Setting form data, form structure type:', typeof formData.form_structure);
      console.log('FormView: Form data keys:', Object.keys(formData));
      
      // Handle random question selection for questionnaires ONLY (not regular survey forms)
      // This feature is only available for questionnaires, which have isQuestionnaire: true
      if (formData.form_structure && 
          typeof formData.form_structure === 'object') {
        
        // Check if this is a questionnaire (not a regular form)
        const isQuestionnaire = formData.form_structure.isQuestionnaire === true || 
                               formData.form_structure.isQuestionnaire === 'true' || 
                               formData.form_structure.isQuestionnaire === 1;
        
        // Only proceed if this is a questionnaire
        if (isQuestionnaire) {
          const enableRandom = formData.form_structure.enableRandomQuestions === true || 
                              formData.form_structure.enableRandomQuestions === 'true' || 
                              formData.form_structure.enableRandomQuestions === 1 ||
                              formData.form_structure.enableRandomQuestions === '1';
          const hasQuestions = Array.isArray(formData.form_structure.questions) && formData.form_structure.questions.length > 0;
          
          console.log('FormView: Questionnaire detected - checking random questions:', {
            isQuestionnaire: true,
            enableRandomQuestions: formData.form_structure.enableRandomQuestions,
            enableRandomQuestionsType: typeof formData.form_structure.enableRandomQuestions,
            enableRandom: enableRandom,
            hasQuestions: hasQuestions,
            questionsCount: formData.form_structure.questions?.length,
            questionsToShow: formData.form_structure.questionsToShow
          });
          
          if (enableRandom && hasQuestions) {
          
          console.log('FormView: ✅ Random questions enabled, processing...');
          
          const sessionKey = `random_questions_${actualFormId}`;
          let selectedQuestions = null;
          
          // Check if questions were already selected for this session
          const storedQuestions = sessionStorage.getItem(sessionKey);
          if (storedQuestions) {
            try {
              selectedQuestions = JSON.parse(storedQuestions);
              console.log('FormView: ✅ Using stored random questions:', selectedQuestions.length);
            } catch (e) {
              console.error('FormView: ❌ Error parsing stored questions:', e);
              sessionStorage.removeItem(sessionKey); // Clear invalid data
            }
          }
          
          // If no stored questions, select random questions
          if (!selectedQuestions) {
            const allQuestions = [...formData.form_structure.questions];
            const questionsToShow = parseInt(formData.form_structure.questionsToShow) || 10;
            
            console.log('FormView: 🔀 Shuffling questions. Total:', allQuestions.length, 'To show:', questionsToShow);
            
            if (allQuestions.length <= questionsToShow) {
              // If we have fewer or equal questions than requested, use all
              selectedQuestions = allQuestions;
              console.log('FormView: Using all questions (not enough for random selection)');
            } else {
              // Shuffle array using Fisher-Yates algorithm
              const shuffled = [...allQuestions];
              for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
              }
              
              // Select first N questions
              selectedQuestions = shuffled.slice(0, questionsToShow);
              console.log('FormView: ✅ Selected', selectedQuestions.length, 'random questions from', allQuestions.length);
            }
            
            // Store in sessionStorage
            sessionStorage.setItem(sessionKey, JSON.stringify(selectedQuestions));
          }
          
          // Create a new form structure object with selected questions
          formData.form_structure = {
            ...formData.form_structure,
            questions: selectedQuestions,
            totalPoints: selectedQuestions.reduce((sum, q) => sum + (q.points || 0), 0)
          };
          
            console.log('FormView: ✅ Updated form structure:');
            console.log('   - Questions count:', formData.form_structure.questions.length);
            console.log('   - Total points:', formData.form_structure.totalPoints);
            console.log('   - First question:', formData.form_structure.questions[0]?.question || formData.form_structure.questions[0]?.label);
          } else {
            console.log('FormView: ⚠️ Random questions not enabled for this questionnaire:', {
              enableRandom,
              hasQuestions,
              questionsCount: formData.form_structure.questions?.length
            });
          }
        } else {
          console.log('FormView: Regular form detected (not a questionnaire) - random questions feature not applicable');
        }
      }
      
      if (typeof formData.form_structure === 'string') {
        try {
          formData.form_structure = JSON.parse(formData.form_structure);
        } catch (e) {
          console.error('Failed to parse form_structure:', e);
          formData.form_structure = [];
        }
      }

      if (Array.isArray(formData.form_structure)) {
        formData.form_structure = normalizeFormStructure(formData.form_structure);
      }
      
      setForm(formData);
      
      // Debug: Log the form structure to see what fields we have
      console.log('FormView: Form loaded with structure:', formData.form_structure);
      if (formData.form_structure?.questions) {
        console.log('FormView: Questions in formData after processing:', {
          count: formData.form_structure.questions.length,
          firstQuestion: formData.form_structure.questions[0]?.question || formData.form_structure.questions[0]?.label,
          enableRandomQuestions: formData.form_structure.enableRandomQuestions
        });
      }
      console.log('FormView: Certificate settings:', {
        enable_certificate: formData.enable_certificate,
        certificate_title: formData.certificate_title,
        certificate_template: formData.certificate_template
      });
      if (formData.form_structure && Array.isArray(formData.form_structure)) {
        console.log('FormView: First few fields:', formData.form_structure.slice(0, 3).map(f => ({
          id: f.id,
          name: f.name,
          type: f.type,
          label: f.label,
          question: f.question,
          hasLabel: !!f.label,
          hasQuestion: !!f.question
        })));
      }
      
      // Initialize session start time for session duration tracking
      setSessionStartTime(new Date());
      
      // Set session end time when user leaves the page
      const handleBeforeUnload = () => {
        setSessionEndTime(new Date());
      };
      
      window.addEventListener('beforeunload', handleBeforeUnload);
      
      // Cleanup function
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    } catch (error) {
      console.error('Load form error:', error);
      toast.error('Failed to load form');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadForm();
    trackFormView();
    collectAnalyticsData();
  }, [actualFormId]);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Multi-step functionality
  // Convert enable_multi_step from SQL Server Bit (1/0) to boolean
  // SQL Server returns Bit type as 1 or 0, not true/false
  const enableMultiStepValue = form?.enable_multi_step !== undefined ? form.enable_multi_step : form?.enableMultiStep;
  const isMultiStep = enableMultiStepValue === true || enableMultiStepValue === 1 || enableMultiStepValue === '1' || enableMultiStepValue === 'true';
  
  const groupSectionsValue = form?.group_sections_with_questions !== undefined ? form.group_sections_with_questions : form?.groupSectionsWithQuestions;
  const groupSectionsWithQuestions = groupSectionsValue === true || groupSectionsValue === 1 || groupSectionsValue === '1' || groupSectionsValue === 'true';
  
  // Determine if this is a questionnaire or regular form (for multi-step logic)
  const isQuestionnaireType = form?.form_structure && typeof form.form_structure === 'object' && form.form_structure.isQuestionnaire;
  
  // For questionnaires with form fields (intro section), we need to handle steps differently
  const hasIntroSection = isQuestionnaireType && form.form_structure?.formFields && form.form_structure.formFields.length > 0;
  
  // Get all fields based on form type
  const allFields = isQuestionnaireType 
    ? (form.form_structure?.questions || [])
    : (Array.isArray(form?.form_structure) ? form.form_structure : []);
  
  // Debug: Log questions when form changes
  useEffect(() => {
    // Reset logging flag when form changes
    window._selectFieldsLogged = false;
    
    if (form?.form_structure?.questions && isQuestionnaireType) {
      console.log('FormView: Rendering with questions:', {
        count: form.form_structure.questions.length,
        enableRandomQuestions: form.form_structure.enableRandomQuestions,
        questionsToShow: form.form_structure.questionsToShow,
        firstQuestion: form.form_structure.questions[0]?.question || form.form_structure.questions[0]?.label
      });
    }
    
    // Debug: Log all select fields for nested dropdown debugging
    if (form && allFields.length > 0) {
      const selectFields = allFields.filter(f => f.type === 'select');
      if (selectFields.length > 0) {
        console.log('🔍 All Select Fields Debug (useEffect):', {
          totalSelectFields: selectFields.length,
          selectFields: selectFields.map(f => ({
            id: f.id,
            name: f.name,
            label: f.label,
            isNested: !!f.parentDropdown,
            parentDropdown: f.parentDropdown,
            hasNestedOptions: !!f.nestedOptions,
            nestedOptionsKeys: f.nestedOptions ? Object.keys(f.nestedOptions) : [],
            options: f.options
          }))
        });
      }
    }
  }, [form?.id, isQuestionnaireType]);
  
  // Helper function to check if a field should be visible based on conditional logic
  const isFieldVisible = (field) => {
    // Check for nested dropdown visibility first
    if (field.type === 'select' && field.parentDropdown) {
      const parentField = allFields.find(f => 
        f.id === field.parentDropdown || 
        f.name === field.parentDropdown ||
        f.id === field.parentDropdown ||
        String(f.id) === String(field.parentDropdown) ||
        String(f.name) === String(field.parentDropdown)
      );
      if (parentField) {
        // Try multiple ways to get the parent value
        const parentValue = formData[parentField.name] || 
                         formData[parentField.id] || 
                         formData[String(parentField.name)] ||
                         formData[String(parentField.id)] ||
                         '';
        // Nested dropdown is only visible if parent has a selected value
        const isVisible = !!parentValue && parentValue !== '';
        
        console.log('isFieldVisible - Nested dropdown check:', {
          fieldId: field.id,
          fieldLabel: field.label,
          parentDropdown: field.parentDropdown,
          parentFieldId: parentField.id,
          parentFieldName: parentField.name,
          parentValue: parentValue,
          isVisible: isVisible,
          formDataKeys: Object.keys(formData),
          formDataParentName: formData[parentField.name],
          formDataParentId: formData[parentField.id]
        });
        
        return isVisible;
      }
      console.warn('isFieldVisible - Parent field not found for nested dropdown:', {
        fieldId: field.id,
        fieldLabel: field.label,
        parentDropdown: field.parentDropdown
      });
      return false; // Parent not found, hide nested dropdown
    }
    
    // Check for conditional field visibility (non-nested dropdowns)
    if (!field.conditionalField || !field.conditionalValue) {
      return true;
    }
    
    // Find the parent field (select, radio, or checkbox)
    const parentField = allFields.find(f => f.id === field.conditionalField || f.name === field.conditionalField);
    if (!parentField) {
      return true; // If parent not found, show field
    }
    
    // Get the selected value from the parent field
    const selectedValue = formData[parentField.name] || formData[parentField.id];
    
    // Handle checkbox fields (array of values)
    if (parentField.type === 'checkbox') {
      const selectedArray = Array.isArray(selectedValue) ? selectedValue : [];
      // Field is visible if the checkbox array includes the conditional value
      return selectedArray.includes(field.conditionalValue);
    }
    
    // Handle select/radio fields (single value)
    // Field is visible only if the selected value matches the conditional value
    return selectedValue === field.conditionalValue;
  };
  
  // Calculate steps based on grouping option
  let totalSteps = 1;
  let stepGroups = [];
  
  if (isMultiStep) {
    if (groupSectionsWithQuestions) {
      // Group sections with their following questions (respecting conditional visibility)
      const visibleFields = allFields.filter(field => 
        field.visible !== false && 
        isFieldVisible(field)
      );
      let currentGroup = { section: null, fields: [] };
      
      visibleFields.forEach(field => {
        if (field.type === 'section') {
          if (currentGroup.section || currentGroup.fields.length > 0) {
            stepGroups.push(currentGroup);
          }
          currentGroup = { section: field, fields: [] };
        } else if (field.type === 'subheading' || field.type === 'content') {
          // Always include subheading and content fields within groups
          currentGroup.fields.push(field);
        } else if (field.type !== 'heading') {
          // Include all other fields (excluding legacy heading fields)
          currentGroup.fields.push(field);
        }
      });
      
      if (currentGroup.section || currentGroup.fields.length > 0) {
        stepGroups.push(currentGroup);
      }
      
      
      totalSteps = hasIntroSection ? stepGroups.length + 1 : stepGroups.length;
    } else {
      // Original behavior: each question is a separate step
      const filteredFields = allFields.filter(field => 
        field.visible !== false && 
        field.type !== 'section' &&
        field.type !== 'subheading' &&
        field.type !== 'content' &&
        isFieldVisible(field)
      );
      totalSteps = hasIntroSection ? filteredFields.length + 1 : filteredFields.length;
    }
  }
  
  // Determine what to show based on current step
  const showIntroOnly = isMultiStep && hasIntroSection && currentStep === 0;
  const showQuestionsOnly = isMultiStep && !showIntroOnly;
  
  // For questions, adjust the index if there's an intro section
  const questionIndex = hasIntroSection ? currentStep - 1 : currentStep;
  
  // Get filtered fields for non-grouped multi-step
  const filteredFields = allFields.filter(field => 
    field.visible !== false && 
    field.type !== 'section' &&
    field.type !== 'subheading' &&
    isFieldVisible(field)
  );
  
  // Get visible fields based on grouping option
  let visibleFields;
  if (isMultiStep && showQuestionsOnly) {
    if (groupSectionsWithQuestions) {
      // Show the current step group (section + questions)
      const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
      const currentGroup = stepGroups[stepIndex];
      if (currentGroup) {
        // Filter fields in the group based on conditional visibility
        const visibleGroupFields = (currentGroup.fields || []).filter(isFieldVisible);
        visibleFields = [currentGroup.section, ...visibleGroupFields].filter(Boolean);
      } else {
        visibleFields = [];
      }
    } else {
      // Original behavior: show single question
      visibleFields = [filteredFields[questionIndex]].filter(Boolean);
    }
  } else {
    // For non-multi-step forms, include all visible fields including subheadings
    visibleFields = allFields.filter(field => 
      field.visible !== false && 
      isFieldVisible(field)
    );
  }
  
  const isLastStep = currentStep === totalSteps - 1;


  // Debug logging
  useEffect(() => {
    if (form) {
      console.log('🔍 Multi-Step Debug:', {
        enable_multi_step: form.enable_multi_step,
        enableMultiStep: form.enableMultiStep,
        isMultiStep: isMultiStep,
        isQuestionnaireType: isQuestionnaireType,
        hasIntroSection: hasIntroSection,
        showIntroOnly: showIntroOnly,
        showQuestionsOnly: showQuestionsOnly,
        allFieldsLength: allFields.length,
        filteredFieldsLength: filteredFields.length,
        currentStep: currentStep,
        questionIndex: questionIndex,
        totalSteps: totalSteps,
        visibleFieldsLength: visibleFields.length
      });
    }
  }, [form, isMultiStep, currentStep, allFields.length, filteredFields.length, visibleFields.length, isQuestionnaireType, totalSteps, hasIntroSection, showIntroOnly, showQuestionsOnly, questionIndex]);

  const handleNext = () => {
    // Validate current step before moving to next
    let hasErrors = false;
    const newErrors = {};
    let currentGroup = null; // Track current group for error messages

    if (showIntroOnly) {
      // Validate introduction form fields
      if (form.form_structure?.formFields) {
        form.form_structure.formFields.forEach(field => {
          if (field.required) {
            const value = formData[field.name || field.id];
            if (!value || (typeof value === 'string' && value.trim() === '')) {
              newErrors[field.name || field.id] = `${field.label} is required`;
              hasErrors = true;
            }
          }
        });
      }
    } else if (showQuestionsOnly) {
      // Validate all fields in current step
      if (groupSectionsWithQuestions) {
        // Validate all fields in the current step group
        const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
        currentGroup = stepGroups[stepIndex];
        
        if (currentGroup && currentGroup.fields && currentGroup.fields.length > 0) {
          // Filter out sections and only get actual input fields
          const fieldsToValidate = currentGroup.fields.filter(field => {
            const isVisible = field.visible !== false;
            const isNotSection = field.type !== 'section';
            const hasNameOrId = field.name || field.id;
            return isVisible && isNotSection && hasNameOrId;
          });
          
          // Only validate fields that are explicitly required
          const requiredFields = fieldsToValidate.filter(field => {
            const req = field.required;
            return req === true || 
                   req === 1 || 
                   req === 'true' || 
                   req === 'required' ||
                   (typeof req === 'string' && req.toLowerCase() === 'true');
          });
          
          requiredFields.forEach((field) => {
            // Use field.name first (same as in renderField/onChange handlers)
            const fieldKey = field.name || field.id;
            if (!fieldKey) return; // Skip if no key
            
            const value = formData[fieldKey];
            
            // Check if value is empty
            let isEmpty = false;
            
            if (field.type === 'checkbox') {
              isEmpty = isSingleAgreementCheckbox(field)
                ? !value
                : (!Array.isArray(value) || value.length === 0);
            } else if (field.type === 'radio' || field.type === 'select') {
              isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
            } else if (field.type === 'rating') {
              isEmpty = !value || value === 0 || value === '';
            } else {
              if (field.type === 'number') {
                isEmpty = value === undefined || value === null || value === '';
              } else {
                isEmpty = value === undefined || value === null || value === '' || (typeof value === 'string' && value.trim() === '');
              }
            }
            
            if (isEmpty) {
              newErrors[fieldKey] = `${field.label || field.question || 'This field'} is required`;
              hasErrors = true;
            }
          });
        }
      } else {
        // Validate single field (original behavior)
        const currentQuestion = filteredFields[questionIndex];
        if (currentQuestion && currentQuestion.required) {
          const fieldKey = currentQuestion.id || currentQuestion.name;
          const value = formData[fieldKey];
          const isCheckboxEmpty = currentQuestion.type === 'checkbox' && (
            isSingleAgreementCheckbox(currentQuestion)
              ? !value
              : (!Array.isArray(value) || value.length === 0)
          );
          if (!value || 
              (Array.isArray(value) && value.length === 0) || 
              (typeof value === 'string' && value.trim() === '') ||
              isCheckboxEmpty) {
            newErrors[fieldKey] = `${currentQuestion.label || currentQuestion.question || 'This question'} is required`;
            hasErrors = true;
          }
        }
      }
    }

    if (hasErrors) {
      setErrors(newErrors);
      
      // Get field labels for error message
      const failedFieldLabels = Object.keys(newErrors).map(key => {
        // Try to find field in current group
        let field = null;
        if (currentGroup?.fields) {
          field = currentGroup.fields.find(f => (f.name || f.id) === key);
        }
        // If not found, try filteredFields
        if (!field) {
          field = filteredFields.find(f => (f.name || f.id) === key);
        }
        return field?.label || field?.question || key;
      });
      
      const errorMessage = failedFieldLabels.length > 0 
        ? `Please fill required fields: ${failedFieldLabels.join(', ')}`
        : 'Please answer all required fields before proceeding';
      
      toast.error(errorMessage);
      
      // Scroll to first error
      const firstErrorKey = Object.keys(newErrors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorKey}"], [id="${firstErrorKey}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        errorElement.focus();
      }
      
      return;
    }

    // Clear errors and move to next step
    setErrors({});
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    // Clear errors when going back
    setErrors({});
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null,
      }));
    }
  };

  const handleFileUpload = (fieldName, files) => {
    const fileArray = Array.from(files);
    
    // Get field configuration for validation
    const field = form?.form_structure?.find(f => f.name === fieldName);
    if (!field) return;
    
    // Validate files
    const maxSize = (field.maxFileSize || 10) * 1024 * 1024; // Convert MB to bytes
    const allowedTypes = field.acceptedFileTypes || [];
    const allowMultiple = field.multipleFiles || false;
    
    // Check if multiple files are allowed
    if (!allowMultiple && fileArray.length > 1) {
      toast.error('Only one file is allowed for this field');
      return;
    }
    
    // Validate each file
    for (const file of fileArray) {
      // Check file size
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is ${field.maxFileSize || 10}MB`);
        return;
      }
      
      // Check file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (allowedTypes.length > 0 && !allowedTypes.includes(fileExtension)) {
        toast.error(`File ${file.name} is not allowed. Accepted types: ${allowedTypes.join(', ')}`);
        return;
      }
    }
    
    // Store files in state
    // If multiple files are allowed, append to existing files (avoiding duplicates); otherwise replace
    setUploadedFiles(prev => {
      if (allowMultiple) {
        const existingFiles = prev[fieldName] || [];
        // Filter out duplicates by comparing file name and size
        const newFiles = fileArray.filter(newFile => 
          !existingFiles.some(existingFile => 
            existingFile.name === newFile.name && existingFile.size === newFile.size
          )
        );
        return {
          ...prev,
          [fieldName]: [...existingFiles, ...newFiles]
        };
      } else {
        return {
          ...prev,
          [fieldName]: fileArray
        };
      }
    });

    // Store file names in form data
    setFormData(prev => {
      if (allowMultiple) {
        const existingFiles = prev[fieldName] ? prev[fieldName].split(', ') : [];
        const newFileNames = fileArray.map(file => file.name);
        // Filter out duplicate file names
        const uniqueFileNames = [...new Set([...existingFiles, ...newFileNames])];
        return {
          ...prev,
          [fieldName]: uniqueFileNames.join(', ')
        };
      } else {
        return {
          ...prev,
          [fieldName]: fileArray.map(file => file.name).join(', ')
        };
      }
    });

    // Reset file input to allow selecting the same file again if needed
    const fileInput = document.getElementById(`file-${fieldName}`);
    if (fileInput) {
      fileInput.value = '';
    }

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null,
      }));
    }
    
    toast.success(`${fileArray.length} file(s) selected successfully`);
  };

  const handleRepeatableFileUpload = (fieldName, files, repeatableField) => {
    const fileArray = Array.from(files);
    
    // Validate files using repeatableField configuration
    const maxSize = (repeatableField.maxFileSize || 10) * 1024 * 1024; // Convert MB to bytes
    const allowedTypes = repeatableField.acceptedFileTypes || [];
    const allowMultiple = repeatableField.multipleFiles || false;
    
    // Check if multiple files are allowed
    if (!allowMultiple && fileArray.length > 1) {
      toast.error('Only one file is allowed for this field');
      return;
    }
    
    // Validate each file
    for (const file of fileArray) {
      // Check file size
      if (file.size > maxSize) {
        toast.error(`File ${file.name} is too large. Maximum size is ${repeatableField.maxFileSize || 10}MB`);
        return;
      }
      
      // Check file type
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (allowedTypes.length > 0 && !allowedTypes.includes(fileExtension)) {
        toast.error(`File ${file.name} is not allowed. Accepted types: ${allowedTypes.join(', ')}`);
        return;
      }
    }
    
    // Store files in state
    // If multiple files are allowed, append to existing files (avoiding duplicates); otherwise replace
    setUploadedFiles(prev => {
      if (allowMultiple) {
        const existingFiles = prev[fieldName] || [];
        // Filter out duplicates by comparing file name and size
        const newFiles = fileArray.filter(newFile => 
          !existingFiles.some(existingFile => 
            existingFile.name === newFile.name && existingFile.size === newFile.size
          )
        );
        return {
          ...prev,
          [fieldName]: [...existingFiles, ...newFiles]
        };
      } else {
        return {
          ...prev,
          [fieldName]: fileArray
        };
      }
    });

    // Store file names in form data
    setFormData(prev => {
      if (allowMultiple) {
        const existingFiles = prev[fieldName] ? prev[fieldName].split(', ') : [];
        const newFileNames = fileArray.map(file => file.name);
        // Filter out duplicate file names
        const uniqueFileNames = [...new Set([...existingFiles, ...newFileNames])];
        return {
          ...prev,
          [fieldName]: uniqueFileNames.join(', ')
        };
      } else {
        return {
          ...prev,
          [fieldName]: fileArray.map(file => file.name).join(', ')
        };
      }
    });

    // Reset file input to allow selecting the same file again if needed
    const fileInput = document.getElementById(`file-${fieldName}`);
    if (fileInput) {
      fileInput.value = '';
    }

    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: null,
      }));
    }
    
    toast.success(`${fileArray.length} file(s) selected successfully`);
  };

  const removeFile = (fieldName, fileIndex) => {
    setUploadedFiles(prev => {
      const updatedFiles = prev[fieldName] || [];
      const newFiles = updatedFiles.filter((_, index) => index !== fileIndex);
      
      setFormData(formData => ({
        ...formData,
        [fieldName]: newFiles.map(file => file.name).join(', ')
      }));
      
      return {
        ...prev,
        [fieldName]: newFiles
      };
    });
  };

  // Helper function to format accept attribute for file input
  const formatAcceptAttribute = (acceptedTypes) => {
    if (!acceptedTypes || acceptedTypes.length === 0) return '';
    // Map file extensions to proper accept format (with dots and MIME types for videos)
    const mimeTypeMap = {
      'mp4': 'video/mp4,.mp4',
      'mov': 'video/quicktime,.mov',
      'avi': 'video/x-msvideo,.avi',
      'mkv': 'video/x-matroska,.mkv',
      'webm': 'video/webm,.webm'
    };
    return acceptedTypes.map(type => {
      if (mimeTypeMap[type.toLowerCase()]) {
        return mimeTypeMap[type.toLowerCase()];
      }
      return `.${type}`;
    }).join(',');
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isValidMapCoords = (v) => (
    v && typeof v === 'object' && typeof v.lat === 'number' && typeof v.lng === 'number'
    && !Number.isNaN(v.lat) && !Number.isNaN(v.lng)
  );

  const validateForm = () => {
    const newErrors = {};

    // Handle questionnaire validation
    if (isQuestionnaire(form) && form.form_structure?.questions) {
      // Validate form fields first
      if (form.form_structure.formFields) {
        form.form_structure.formFields.forEach((field) => {
          const fieldKey = field.name || field.id;
          const value = formData[fieldKey];

          if (field.required) {
            if (field.type === 'mapLocation') {
              if (!isValidMapCoords(value)) {
                newErrors[fieldKey] = `${field.label} is required`;
              }
            } else if (!value || value === '') {
              newErrors[fieldKey] = `${field.label} is required`;
            }
          }

          // Type-specific validation
          if (value && value !== '' && field.type !== 'mapLocation') {
            if (field.type === 'email') {
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(value)) {
                newErrors[fieldKey] = 'Please enter a valid email address';
              }
            } else if (field.type === 'number') {
              if (isNaN(Number(value))) {
                newErrors[fieldKey] = 'Please enter a valid number';
              }
            }
          }
        });
      }

      // Validate questions (only visible questions)
      const visibleQuestions = form.form_structure.questions.filter(q => q.visible !== false);
      visibleQuestions.forEach((question) => {
        if (question.required) {
          const value = formData[question.id];
          if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
            newErrors[question.id] = 'This question is required';
          }
        }
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    // Handle regular form validation
    form.form_structure.forEach(field => {
      // Handle repeatable group fields
      if (field.type === 'repeatable' && field.repeatableFields) {
        const instances = repeatableInstances[field.id] || [0];
        instances.forEach((instanceIndex) => {
          field.repeatableFields.forEach(repeatableField => {
            const fieldName = `${field.id}_${instanceIndex}_${repeatableField.name}`;
            const value = formData[fieldName];

            // Check required fields
            if (repeatableField.required) {
              if (repeatableField.type === 'checkbox') {
                if (isSingleAgreementCheckbox(repeatableField)) {
                  if (!value) {
                    newErrors[fieldName] = `${repeatableField.label} is required`;
                  }
                } else if (!Array.isArray(value) || value.length === 0) {
                  newErrors[fieldName] = `${repeatableField.label} is required`;
                }
              } else if (repeatableField.type === 'rating') {
                if (!value || value === 0) {
                  newErrors[fieldName] = `${repeatableField.label} is required`;
                }
              } else if (repeatableField.type === 'mapLocation') {
                if (!isValidMapCoords(value)) {
                  newErrors[fieldName] = `${repeatableField.label} is required`;
                }
              } else if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
                newErrors[fieldName] = `${repeatableField.label} is required`;
              }
            }

            // Type-specific validation
            if (value && value !== '' && value !== 0 && repeatableField.type !== 'mapLocation') {
              if (repeatableField.type === 'rating') {
                const maxRating = repeatableField.maxRating || 5;
                if (value < 1 || value > maxRating) {
                  newErrors[fieldName] = `Rating must be between 1 and ${maxRating}`;
                }
              } else if (repeatableField.type === 'email') {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                  newErrors[fieldName] = 'Please enter a valid email address';
                }
              } else if (repeatableField.type === 'number') {
                if (isNaN(Number(value))) {
                  newErrors[fieldName] = 'Please enter a valid number';
                }
              }
            }
          });
        });
      }
      // Handle contact block fields
      else if (field.type === 'contact' && field.contactFields) {
        field.contactFields.forEach(contactField => {
          const value = formData[contactField.name];

          // Check required fields
          if (contactField.required && (!value || value === '' || (Array.isArray(value) && value.length === 0))) {
            newErrors[contactField.name] = `${contactField.label} is required`;
          }

          // Type-specific validation
          if (value && value !== '') {
            switch (contactField.type) {
              case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                  newErrors[contactField.name] = 'Please enter a valid email address';
                }
                break;
              case 'number':
                if (isNaN(Number(value))) {
                  newErrors[contactField.name] = 'Please enter a valid number';
                }
                break;
            }
            
            // Check for custom validation patterns (for contact fields)
            if (contactField.validation && contactField.validation.pattern) {
              const regex = new RegExp(contactField.validation.pattern);
              if (!regex.test(value)) {
                newErrors[contactField.name] = contactField.validation.message || 'Please enter a valid value';
              }
            }
          }
        });
      } else {
        // Handle regular fields
        const value = formData[field.name];

        // Check required fields
        if (field.required) {
          if (field.type === 'mapLocation') {
            if (!isValidMapCoords(value)) {
              newErrors[field.name] = `${field.label} is required`;
            }
          } else if (isSingleAgreementCheckbox(field)) {
            if (!value) {
              newErrors[field.name] = `${getAgreementText(field)} is required`;
            }
          } else if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
            newErrors[field.name] = `${field.label} is required`;
          }
        }

        // Check file upload requirements
        if (field.type === 'file' && field.required) {
          const files = uploadedFiles[field.name] || [];
          if (files.length === 0) {
            newErrors[field.name] = `${field.label} is required`;
          }
        }

        // Type-specific validation
        if (value && value !== '' && field.type !== 'mapLocation') {
          switch (field.type) {
            case 'email':
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
              if (!emailRegex.test(value)) {
                newErrors[field.name] = 'Please enter a valid email address';
              }
              break;
            case 'number':
              if (isNaN(Number(value))) {
                newErrors[field.name] = 'Please enter a valid number';
              }
              break;
          }
          
          // Check for custom validation patterns
          if (field.validation && field.validation.pattern) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(value)) {
              newErrors[field.name] = field.validation.message || 'Please enter a valid value';
            }
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get all fields including nested ones (repeatable groups, contact fields)
  const getAllFields = (fields) => {
    const result = [];
    if (!Array.isArray(fields)) return result;
    
    fields.forEach(field => {
      if (field) {
        result.push(field);
        
        // Add repeatable group fields
        if (field.type === 'repeatable' && Array.isArray(field.repeatableFields)) {
          field.repeatableFields.forEach(repeatableField => {
            if (repeatableField) {
              result.push(repeatableField);
            }
          });
        }
        
        // Add contact block fields
        if (field.type === 'contact' && Array.isArray(field.contactFields)) {
          field.contactFields.forEach(contactField => {
            if (contactField) {
              result.push(contactField);
            }
          });
        }
      }
    });
    
    return result;
  };

  // Load per-option booking counts for dropdowns that opt in (showOptionBookingCounts)
  useEffect(() => {
    if (!form || !actualFormId) return;
    const structure = isQuestionnaireType
      ? (form.form_structure?.questions || [])
      : (Array.isArray(form.form_structure) ? form.form_structure : []);
    const fields = getAllFields(structure);
    const targets = fields.filter((f) => f.type === 'select' && f.showOptionBookingCounts);
    if (targets.length === 0) {
      setOptionBookingCounts({});
      return;
    }
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(
        targets.map(async (f) => {
          try {
            const res = await responsesAPI.getOptionCounts(actualFormId, f.id);
            const counts = res.data?.data?.counts;
            if (counts && typeof counts === 'object') {
              next[f.id] = counts;
            }
          } catch (e) {
            console.warn('Could not load option counts for field', f.id, e);
          }
        })
      );
      if (!cancelled) setOptionBookingCounts(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [form?.id, actualFormId, isQuestionnaireType]);

  const handleSubmit = async (e, { skipValidation = false } = {}) => {
    e.preventDefault();

    if (!skipValidation && !validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      
      // Upload files first if any
      // Normalize form data - ensure checkbox fields are always arrays
      const normalizedFormData = { ...formData };
      
      // Find all checkbox fields and ensure they're arrays
      const formStructure = isQuestionnaire(form) 
        ? (form?.form_structure?.formFields || [])
        : (Array.isArray(form?.form_structure) ? form.form_structure : []);
      const allFields = getAllFields(formStructure);
      allFields.forEach(field => {
        if (field.type === 'checkbox') {
          const fieldName = field.name || field.id;
          const value = normalizedFormData[fieldName];
          if (isSingleAgreementCheckbox(field)) {
            normalizedFormData[fieldName] = value === true || value === 'true' || value === 1;
            return;
          }
          // If value is not an array, convert it or set to empty array
          if (!Array.isArray(value)) {
            if (value === undefined || value === null || value === '') {
              normalizedFormData[fieldName] = [];
            } else if (typeof value === 'string') {
              // If it's a comma-separated string, split it
              normalizedFormData[fieldName] = value.split(',').map(v => v.trim()).filter(v => v);
            } else {
              // Convert single value to array
              normalizedFormData[fieldName] = [value];
            }
          }
        }
      });
      
      const uploadedFileData = { ...normalizedFormData };
      
      for (const [fieldName, files] of Object.entries(uploadedFiles)) {
        if (files && files.length > 0) {
          const uploadedFilenames = [];
          
          for (const file of files) {
            try {
              const formData = new FormData();
              formData.append('file', file);
              
              // Get API base URL (same logic as api.js)
              const getApiBaseUrl = () => {
                if (process.env.REACT_APP_API_URL) {
                  return process.env.REACT_APP_API_URL;
                }
                const hostname = window.location.hostname;
                const protocol = window.location.protocol;
                const port = window.location.port;
                if (hostname === 'localhost' || hostname === '127.0.0.1') {
                  return 'http://localhost:5000/api';
                }
                const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
                return `${baseUrl}/api`;
              };
              
              const apiBaseUrl = getApiBaseUrl();
              const uploadUrl = `${apiBaseUrl}/upload/file`;
              
              console.log('Uploading file:', file.name, 'to:', uploadUrl);
              
              const response = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
              });
              
              if (!response.ok) {
                const errorText = await response.text();
                console.error('Upload response error:', response.status, errorText);
                throw new Error(`Upload failed: ${response.statusText} (${response.status})`);
              }
              
              const result = await response.json();
              if (result.success) {
                uploadedFilenames.push(result.data.filename);
                console.log('File uploaded successfully:', file.name, '->', result.data.filename, 'Total files so far:', uploadedFilenames.length);
              } else {
                throw new Error(result.error || 'Upload failed');
              }
            } catch (uploadError) {
              console.error('File upload error:', uploadError);
              console.error('File details:', {
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
              });
              toast.error(`Failed to upload ${file.name}: ${uploadError.message}`);
              setSubmitting(false);
              return;
            }
          }
          
          uploadedFileData[fieldName] = uploadedFilenames.join(', ');
        }
      }
      
      // Calculate score if this is a questionnaire
      let calculatedScore = null;
      if (isQuestionnaire(form)) {
        calculatedScore = calculateQuestionnaireScore(form.form_structure, uploadedFileData);
        setScoreData(calculatedScore);
        console.log('Questionnaire score calculated:', calculatedScore);
      }
      
      // Calculate session duration
      const endTime = new Date().toISOString();
      setSessionEndTime(endTime);
      const duration = calculateDuration(sessionStartTime, endTime);

      // Prepare analytics data
      const analyticsPayload = {
        session_id: sessionId,
        start_datetime: sessionStartTime,
        end_datetime: endTime,
        duration_seconds: duration.total_seconds,
        duration_formatted: duration.formatted,
        ...analyticsData,
      };

      // Submit form with uploaded file data, score, and analytics
      const submitResponse = await responsesAPI.submit({
        form_id: actualFormId,
        response_data: {
          ...uploadedFileData,
          // Analytics data
          _analytics: analyticsPayload,
          // Store score data for questionnaires
          ...(calculatedScore && {
            _questionnaire_score: calculatedScore.earnedPoints,
            _questionnaire_total: calculatedScore.totalPoints,
            _questionnaire_percentage: calculatedScore.percentage,
            _questionnaire_grade: calculatedScore.grade,
            _questionnaire_remark: calculatedScore.remark,
            _questionnaire_results: calculatedScore.questionResults,
          }),
        },
      });
      
      setSubmitted(true);
      setCertificateData(null);
      setCertificateError(null);
      setCertificateGenerating(false);
      
      // Generate certificate for questionnaire completion
      console.log('Submit response:', submitResponse);
      console.log('Submit response data:', submitResponse.data);
      console.log('Calculated score:', calculatedScore);
      
      // Try different paths to find response ID
      const responseId = submitResponse.data?.data?.response_id || 
                        submitResponse.data?.data?.id || 
                        submitResponse.data?.response_id || 
                        submitResponse.data?.id;
      
      console.log('Response ID:', responseId);
      
      if (calculatedScore) {
        // Only generate certificate if it's enabled for this form
        if (form?.enable_certificate && responseId) {
          try {
            setCertificateGenerating(true);
            console.log('Generating certificate for form:', actualFormId, 'response:', responseId);
            const certificateResponse = await certificatesAPI.generateCertificate(actualFormId, responseId);
            console.log('Certificate response:', certificateResponse.data);
            setCertificateData(certificateResponse.data.data);
            setCertificateError(null);
            toast.success('Questionnaire submitted successfully! Your certificate is ready below.');
          } catch (certError) {
            console.error('Certificate generation error:', certError);
            console.error('Error details:', certError.response?.data);
            const message = certError.response?.data?.error || certError.message || 'Unknown error';
            setCertificateError(`Certificate generation failed: ${message}`);
            toast.success('Questionnaire submitted successfully!');
            toast.error('We were unable to generate the certificate automatically. Please try again or contact support.');
          } finally {
            setCertificateGenerating(false);
          }
        } else if (form?.enable_certificate && !responseId) {
          toast.success('Questionnaire submitted successfully!');
          setCertificateError('We could not locate your response ID to generate the certificate automatically.');
        } else {
          toast.success('Questionnaire submitted successfully!');
        }
      } else {
        toast.success('Form submitted successfully!');
      }
    } catch (error) {
      // Show specific error message if available
      let errorMessage = 'Failed to submit form';
      
      // Debug logging
      console.error('=== SUBMIT ERROR DEBUG ===');
      console.error('Error object:', error);
      console.error('Error.response:', error.response);
      console.error('Error.response?.status:', error.response?.status);
      console.error('Error.response?.data:', error.response?.data);
      console.error('Error.message:', error.message);
      
      // Extract error message from response
      if (error.response && error.response.data) {
        const data = error.response.data;
        const status = error.response.status;
        
        console.error('Response data:', data);
        console.error('Response status:', status);
        
        // Check for quota limit error (403 status)
        if (status === 403) {
          // Priority: message > error > string data > fallback
          if (data.message && typeof data.message === 'string') {
            errorMessage = data.message;
          } else if (data.error && typeof data.error === 'string') {
            errorMessage = data.error;
          } else if (typeof data === 'string') {
            errorMessage = data;
          } else {
            errorMessage = 'Daily submission limit exceeded. Please try again tomorrow.';
          }
        } else {
          // For other errors
          if (data.message && typeof data.message === 'string') {
            errorMessage = data.message;
          } else if (data.error && typeof data.error === 'string') {
            errorMessage = data.error;
          } else if (typeof data === 'string') {
            errorMessage = data;
          } else {
            errorMessage = `Failed to submit form${status ? ` (Status: ${status})` : ''}`;
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Ensure quota messages mention tomorrow
      const lowerMessage = errorMessage.toLowerCase();
      if ((lowerMessage.includes('daily') || lowerMessage.includes('quota') || lowerMessage.includes('limit')) 
          && !lowerMessage.includes('tomorrow')) {
        errorMessage += ' Please try again tomorrow.';
      }
      
      console.error('Final error message:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Helper function to check if a field is a contact block
  const isContactBlock = (field) => {
    return field.type === 'contact';
  };

  const getSelectOptionDisplayText = (field, option) => {
    if (!field?.showOptionBookingCounts) return option;
    const n = optionBookingCounts[field.id]?.[option];
    if (typeof n === 'number') {
      const cap = field.maxPerOption;
      if (cap && cap > 0) {
        return `${option} (${n} of ${cap} booked)`;
      }
      return `${option} (${n} booked)`;
    }
    return option;
  };

  const renderField = (field) => {
    // Debug: Log field data to see what we're working with
    console.log('FormView: renderField called for field:', {
      id: field.id,
      name: field.name,
      type: field.type,
      label: field.label,
      question: field.question,
      hasLabel: !!field.label,
      hasQuestion: !!field.question,
      labelOrQuestion: field.label || field.question
    });
    
    const fieldProps = {
      name: field.name,
      required: field.required,
      value: formData[field.name] || '',
      onChange: (e) => handleInputChange(field.name, e.target.value),
      fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : undefined,
      bold: field.bold || false,
      italic: field.italic || false,
      inputPaddingLeft: parseInt(field.inputPaddingLeft) || 16
    };
    
    // Debug logging for styling
    if (field.fontFamily || field.bold || field.italic || field.inputPaddingLeft) {
      console.log('FormView: Field with styling:', field.label, {
        fontFamily: field.fontFamily,
        bold: field.bold,
        italic: field.italic,
        inputPaddingLeft: field.inputPaddingLeft
      });
    }
    
    // Debug logging (only for fields with styling)
    if (field.fontFamily && field.fontFamily !== 'default') {
      console.log('FormView: Field with font family:', field.label, field.fontFamily);
    }
    if (field.marginTop || field.marginBottom || field.paddingTop || field.paddingBottom) {
      console.log('FormView: Field with spacing:', field.label, {
        marginTop: field.marginTop,
        marginBottom: field.marginBottom,
        paddingTop: field.paddingTop,
        paddingBottom: field.paddingBottom
      });
    }

    switch (field.type) {
      case 'heading':
        // Legacy heading block is deprecated in favor of 'section'
        // Do not render to avoid duplicate headings when both exist
        return null;
      
      case 'text':
      case 'email':
      case 'number':
      case 'date':
        return (
          <FieldInput
            type={field.type}
            {...fieldProps}
            placeholder={field.placeholder}
          />
        );

      case 'textarea':
        return (
          <FieldTextarea
            {...fieldProps}
            placeholder={field.placeholder}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
          />
        );

      case 'mapLocation': {
        const raw = formData[field.name];
        const mapVal = raw && typeof raw === 'object' && typeof raw.lat === 'number' && typeof raw.lng === 'number'
          ? raw
          : null;
        return (
          <MapLocationField
            value={mapVal}
            onChange={(coords) => handleInputChange(field.name, coords)}
          />
        );
      }

      case 'select':
        // Check if this is a nested dropdown
        const isNestedDropdown = field.parentDropdown;
        let parentField = null;
        let parentValue = null;
        let availableNestedOptions = [];
        
        // Log all select fields on first render (use a flag to avoid spam)
        if (!window._selectFieldsLogged) {
          const allSelectFields = allFields.filter(f => f.type === 'select');
          console.log('🔍 All Select Fields Debug (on render):', {
            totalSelectFields: allSelectFields.length,
            selectFields: allSelectFields.map(f => ({
              id: f.id,
              name: f.name,
              label: f.label,
              isNested: !!f.parentDropdown,
              parentDropdown: f.parentDropdown,
              hasNestedOptions: !!f.nestedOptions,
              nestedOptionsKeys: f.nestedOptions ? Object.keys(f.nestedOptions) : [],
              options: f.options
            }))
          });
          window._selectFieldsLogged = true;
        }
        
        console.log('Select field render:', {
          fieldId: field.id,
          fieldName: field.name,
          fieldLabel: field.label,
          isNestedDropdown: !!isNestedDropdown,
          parentDropdown: field.parentDropdown,
          hasNestedOptions: !!field.nestedOptions,
          fieldOptions: field.options
        });
        
        if (isNestedDropdown) {
          // Try multiple ways to find the parent field
          parentField = allFields.find(f => 
            f.id === field.parentDropdown || 
            f.name === field.parentDropdown ||
            String(f.id) === String(field.parentDropdown) ||
            String(f.name) === String(field.parentDropdown)
          );
          
          console.log('Nested dropdown - parent field search:', {
            parentDropdownValue: field.parentDropdown,
            allFieldsCount: allFields.length,
            parentFieldFound: !!parentField,
            parentFieldId: parentField?.id,
            parentFieldName: parentField?.name,
            parentFieldNestedOptions: parentField?.nestedOptions
          });
          
          if (parentField) {
            // Try multiple ways to get the parent value
            parentValue = formData[parentField.name] || 
                         formData[parentField.id] || 
                         formData[String(parentField.name)] ||
                         formData[String(parentField.id)] ||
                         '';
            
            console.log('Nested dropdown - parent value:', {
              parentFieldName: parentField.name,
              parentFieldId: parentField.id,
              formDataKeys: Object.keys(formData),
              parentValueFromName: formData[parentField.name],
              parentValueFromId: formData[parentField.id],
              finalParentValue: parentValue,
              nestedOptionsObject: parentField.nestedOptions,
              nestedOptionsKeys: parentField.nestedOptions ? Object.keys(parentField.nestedOptions) : []
            });
            
            // Get nested options based on parent selection
            if (parentValue) {
              availableNestedOptions = parentField.nestedOptions?.[parentValue] || [];
              console.log('Nested dropdown - available options:', {
                parentValue: parentValue,
                availableNestedOptions: availableNestedOptions,
                nestedOptionsForValue: parentField.nestedOptions?.[parentValue]
              });
            } else {
              console.log('Nested dropdown - no parent value selected yet');
            }
          } else {
            // Parent field not found - this shouldn't happen, but hide the dropdown
            console.warn('Nested dropdown parent field not found:', {
              parentDropdown: field.parentDropdown,
              allFieldIds: allFields.map(f => ({ id: f.id, name: f.name, type: f.type }))
            });
            return null;
          }
        }
        
        // If nested dropdown and parent not selected, hide it
        if (isNestedDropdown && (!parentValue || parentValue === '')) {
          console.log('Nested dropdown hidden - no parent value:', {
            fieldId: field.id,
            fieldLabel: field.label,
            parentValue: parentValue,
            parentFieldId: parentField?.id
          });
          return null;
        }
        
        // If nested dropdown and has parent value but no options, log warning
        if (isNestedDropdown && parentValue && availableNestedOptions.length === 0) {
          console.warn('Nested dropdown has parent value but no options available:', {
            fieldId: field.id,
            fieldLabel: field.label,
            parentValue: parentValue,
            parentFieldId: parentField?.id,
            parentFieldNestedOptions: parentField?.nestedOptions,
            nestedOptionsKeys: parentField?.nestedOptions ? Object.keys(parentField.nestedOptions) : []
          });
        }
        
        // Handle parent dropdown onChange - clear nested dropdowns when parent changes
        const handleParentDropdownChange = (e) => {
          const selectedValue = e.target.value;
          handleInputChange(field.name, selectedValue);
          
          // Clear dependent conditional fields when selection changes
          const conditionalFields = field.conditionalOptions?.[selectedValue] || [];
          const otherValues = field.options?.filter(opt => opt !== selectedValue) || [];
          otherValues.forEach(otherValue => {
            const fieldsToClear = field.conditionalOptions?.[otherValue] || [];
            fieldsToClear.forEach(fieldId => {
              const fieldToClear = allFields.find(f => f.id === fieldId);
              if (fieldToClear) {
                handleInputChange(fieldToClear.name || fieldToClear.id, '');
              }
            });
          });
          
          // Clear nested dropdowns that depend on this field when selection changes
          if (field.nestedOptions) {
            const dependentFields = allFields.filter(f => 
              f.type === 'select' && 
              (f.parentDropdown === field.id ||
               f.parentDropdown === field.name ||
               String(f.parentDropdown) === String(field.id) ||
               String(f.parentDropdown) === String(field.name))
            );
            dependentFields.forEach(depField => {
              handleInputChange(depField.name || depField.id, '');
            });
          }
        };

        // Handle nested dropdown onChange
        const handleNestedDropdownChange = (e) => {
          handleInputChange(field.name, e.target.value);
        };

        return (
          <>
          <FieldSelect 
            {...fieldProps} 
            value={isNestedDropdown ? (formData[field.name] || '') : fieldProps.value}
            disabled={isNestedDropdown && !parentValue}
            onChange={isNestedDropdown ? handleNestedDropdownChange : handleParentDropdownChange}
          >
            <option value="">Select an option</option>
            {isNestedDropdown && availableNestedOptions.length > 0
              ? availableNestedOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {getSelectOptionDisplayText(field, option)}
                  </option>
                ))
              : (field.options?.map((option, index) => (
                  <option key={index} value={option}>
                    {getSelectOptionDisplayText(field, option)}
                  </option>
                )) || [])
            }
          </FieldSelect>
            {field.allowComment && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  color: '#374151', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {field.commentLabel || 'Additional Comments'}
                </label>
                <FieldTextarea
                  name={`${field.name}_comment`}
                  value={formData[`${field.name}_comment`] || ''}
                  onChange={(e) => handleInputChange(`${field.name}_comment`, e.target.value)}
                  placeholder="Enter your comments here..."
                  rows={3}
                />
              </div>
            )}
          </>
        );

      case 'radio':
        return (
          <>
          <RadioGroup
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
          >
            {field.options?.map((option, index) => (
              <RadioItem key={index}>
                <RadioInput
                  type="radio"
                  name={field.name}
                  value={option}
                  checked={formData[field.name] === option}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                />
                <span>{option}</span>
              </RadioItem>
            ))}
          </RadioGroup>
            {field.allowComment && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  color: '#374151', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {field.commentLabel || 'Additional Comments'}
                </label>
                <FieldTextarea
                  name={`${field.name}_comment`}
                  value={formData[`${field.name}_comment`] || ''}
                  onChange={(e) => handleInputChange(`${field.name}_comment`, e.target.value)}
                  placeholder="Enter your comments here..."
                  rows={3}
                />
              </div>
            )}
          </>
        );

      case 'checkbox':
        if (isSingleAgreementCheckbox(field)) {
          return (
            <>
              <AgreementCheckboxField
                field={field}
                checked={!!formData[field.name]}
                onChange={(value) => handleInputChange(field.name, value)}
              />
              {field.allowComment && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '14px', 
                    color: '#374151', 
                    marginBottom: '8px',
                    fontWeight: '500'
                  }}>
                    {field.commentLabel || 'Additional Comments'}
                  </label>
                  <FieldTextarea
                    name={`${field.name}_comment`}
                    value={formData[`${field.name}_comment`] || ''}
                    onChange={(e) => handleInputChange(`${field.name}_comment`, e.target.value)}
                    placeholder="Enter your comments here..."
                    rows={3}
                  />
                </div>
              )}
            </>
          );
        }
        const checkboxValues = formData[field.name] || [];
        return (
          <>
          <CheckboxGroup
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
            checkboxLayout={field.checkboxLayout || 'vertical'}
          >
            {field.options?.map((option, index) => (
              <CheckboxItem key={index}>
                <CheckboxInput
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const currentValues = checkboxValues;
                    if (e.target.checked) {
                      handleInputChange(field.name, [...currentValues, option]);
                    } else {
                      handleInputChange(
                        field.name,
                        currentValues.filter(val => val !== option)
                      );
                    }
                  }}
                />
                <span>{option}</span>
              </CheckboxItem>
            ))}
          </CheckboxGroup>
            {field.allowComment && (
              <div style={{ marginTop: '12px' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  color: '#374151', 
                  marginBottom: '8px',
                  fontWeight: '500'
                }}>
                  {field.commentLabel || 'Additional Comments'}
                </label>
                <FieldTextarea
                  name={`${field.name}_comment`}
                  value={formData[`${field.name}_comment`] || ''}
                  onChange={(e) => handleInputChange(`${field.name}_comment`, e.target.value)}
                  placeholder="Enter your comments here..."
                  rows={3}
                />
              </div>
            )}
          </>
        );

      case 'calculation':
        return (
          <CalculationField
            field={field}
            value={formData[field.name] || 0}
            onChange={(value) => handleInputChange(field.name, value)}
            formData={formData}
            showFormula={false}
            showHelp={false}
          />
        );

      case 'file':
        const files = uploadedFiles[field.name] || [];
        return (
          <div>
            <FileUploadContainer
              onClick={() => document.getElementById(`file-${field.name}`).click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.currentTarget.classList.add('drag-over');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove('drag-over');
                const droppedFiles = e.dataTransfer.files;
                handleFileUpload(field.name, droppedFiles);
              }}
            >
              <FileUploadInput
                id={`file-${field.name}`}
                type="file"
                multiple={field.multipleFiles}
                accept={formatAcceptAttribute(field.acceptedFileTypes)}
                onChange={(e) => handleFileUpload(field.name, e.target.files)}
              />
              <FileUploadText>
                <FileUploadIcon>
                  <FiUpload />
                </FileUploadIcon>
                <div>
                  <strong>Click to upload</strong> or drag and drop
                </div>
                <div style={{ fontSize: '14px' }}>
                  {field.acceptedFileTypes && field.acceptedFileTypes.length > 0 && (
                    <>Accepted types: {field.acceptedFileTypes.join(', ')}</>
                  )}
                  {field.maxFileSize && (
                    <> • Max size: {formatFileSize(field.maxFileSize * 1024 * 1024)}</>
                  )}
                </div>
              </FileUploadText>
            </FileUploadContainer>
            
            {files.length > 0 && (
              <FileList>
                {files.map((file, index) => (
                  <FileItem key={index}>
                    <FileInfo>
                      <FiUpload />
                      <div>
                        <div>{file.name}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {formatFileSize(file.size)}
                        </div>
                      </div>
                    </FileInfo>
                    <RemoveFileButton type="button" onClick={() => removeFile(field.name, index)}>
                      Remove
                    </RemoveFileButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </div>
        );

      case 'rating':
        console.log('FormView: Rendering rating field:', field);
        const rating = formData[field.name] || 0;
        const maxRating = field.maxRating || 5;
        const ratingLabels = field.ratingLabels || [];
        const ratingIcon = field.ratingIcon || 'star';
        // Use Unicode characters that respond to CSS color where possible
        const iconMap = { 
          star: { filled: '★', empty: '☆' },
          heart: { filled: '❤', empty: '♡' },
          thumbs: { filled: '👍', empty: '👍' }, // Emoji - will use filter
          face: { filled: '😊', empty: '😊' }, // Emoji - will use filter
          fire: { filled: '🔥', empty: '🔥' }, // Emoji - will use filter
          check: { filled: '✔', empty: '☐' }
        };
        const emotionFaces = ['😢', '😐', '😊', '😄', '😍']; // Sad, Neutral, Happy, Very Happy, Love
        
        // Check if icon is an emoji that needs filter (doesn't respond to CSS color)
        const isEmojiIcon = (icon) => {
          return ['thumbs', 'face', 'fire'].includes(icon) || icon === 'emotion';
        };
        
        // Return the appropriate icon for each position, taking into account filled state
        const getIconForIndex = (ratingValue, isFilled) => {
          if (ratingIcon === 'emotion') {
            const faceIndex = Math.min(Math.max(ratingValue - 1, 0), emotionFaces.length - 1);
            return emotionFaces[faceIndex];
          }
          const iconSet = iconMap[ratingIcon] || iconMap.star;
          return isFilled ? iconSet.filled : iconSet.empty;
        };
        
        return (
          <RatingContainer
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
          >
            <StarContainer>
              {Array.from({ length: maxRating }, (_, index) => {
                const isFilled = index < rating;
                const iconNeedsFilter = isEmojiIcon(ratingIcon) || (ratingIcon === 'emotion');
                return (
                <StarButton
                  key={index}
                  type="button"
                    filled={isFilled}
                  onClick={() => handleInputChange(field.name, index + 1)}
                  style={{ 
                    fontSize: '24px',
                      transform: isFilled ? 'rotateY(360deg)' : 'rotateY(0deg)',
                      transition: 'transform 0.6s ease',
                      // For emoji icons, use filter to make them grey when not filled
                      ...(iconNeedsFilter && !isFilled ? {
                        filter: 'grayscale(100%) opacity(0.4)',
                        transition: 'filter 0.2s ease, transform 0.6s ease'
                      } : {}),
                      ...(iconNeedsFilter && isFilled ? {
                        filter: 'none',
                        transition: 'filter 0.2s ease, transform 0.6s ease'
                      } : {})
                    }}
                  >
                    {getIconForIndex(index + 1, isFilled)}
                </StarButton>
                );
              })}
            </StarContainer>
            {rating > 0 && (
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                Selected: {rating}{rating !== 1 ? '' : ''}
                {ratingLabels[rating - 1] && ` (${ratingLabels[rating - 1]})`}
              </div>
            )}
          </RatingContainer>
        );

      case 'section':
        const sectionHeadingStyle = {
          fontSize: field.level === 1 ? '28px' : field.level === 2 ? '24px' : field.level === 3 ? '20px' : field.level === 4 ? '18px' : field.level === 5 ? '16px' : '14px',
          fontWeight: field.bold ? '700' : '600',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#1f2937',
          textAlign: field.alignment || 'left',
          margin: '0 0 8px 0',
          padding: '0'
        };
        
        // Debug section styling
        console.log('FormView: Rendering section field:', field.label);
        console.log('FormView: Section field data:', {
          bold: field.bold,
          alignment: field.alignment,
          fontFamily: field.fontFamily,
          textColor: field.textColor,
          level: field.level
        });
        console.log('FormView: Section heading style:', sectionHeadingStyle);
        
        return (
          <div style={{ margin: '24px 0 12px 0' }}>
            <div style={sectionHeadingStyle}>{field.label || field.question}</div>
            {field.sectionSubtitle && (
              <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: field.sectionDivider ? '12px' : '0' }}>
                {field.sectionSubtitle}
              </div>
            )}
            {field.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
          </div>
        );

      case 'subheading':
        const subheadingStyle = {
          fontSize: field.level === 1 ? '20px' : field.level === 2 ? '18px' : field.level === 3 ? '16px' : field.level === 4 ? '14px' : field.level === 5 ? '12px' : '14px',
          fontWeight: field.bold ? '600' : '500',
          fontStyle: field.italic ? 'italic' : 'normal',
          textDecoration: field.underline ? 'underline' : 'none',
          fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
          color: field.textColor || '#374151',
          textAlign: field.alignment || 'left',
          margin: '0 0 6px 0',
          padding: '0'
        };
        
        return (
          <div style={{ margin: '16px 0 8px 0' }}>
            <div style={subheadingStyle}>{field.label || field.question}</div>
          </div>
        );

      case 'content':
        return (
          <div style={{ 
            margin: '16px 0', 
            padding: '16px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word'
            }}>
              {field.content || field.label || ''}
            </div>
          </div>
        );

      case 'table':
        return (
          <TableField
            field={field}
            value={formData[field.name] || []}
            onChange={(value) => handleInputChange(field.name, value)}
            formData={formData}
          />
        );

      case 'matrix':
        return (
          <MatrixField
            field={field}
            value={formData[field.name] || {}}
            onChange={(value) => handleInputChange(field.name, value)}
          />
        );

      case 'slider':
        return (
          <SliderContainer
            fontFamily={field.fontFamily}
            bold={field.bold}
            italic={field.italic}
            inputPaddingLeft={field.inputPaddingLeft}
          >
            <SliderInput
              type="range"
              min={field.min ?? 0}
              max={field.max ?? 10}
              step={field.step ?? 1}
              value={formData[field.name] ?? field.min ?? 0}
              onChange={(e) => handleInputChange(field.name, Number(e.target.value))}
            />
            <SliderValue>
              {formData[field.name] ?? field.min ?? 0}
            </SliderValue>
          </SliderContainer>
        );

      case 'repeatable':
        // Initialize instances if not already initialized
        if (!repeatableInstances[field.id]) {
          setRepeatableInstances(prev => ({
            ...prev,
            [field.id]: [0] // Start with one instance
          }));
          return null; // Return null on first render to initialize
        }

        const instances = repeatableInstances[field.id] || [0];
        const continueStates = repeatableContinueStates[field.id] || {};

        const addRepeatableInstance = (fieldId) => {
          setRepeatableInstances(prev => ({
            ...prev,
            [fieldId]: [...(prev[fieldId] || []), (prev[fieldId]?.length || 0)]
          }));
          setRepeatableContinueStates(prev => ({
            ...prev,
            [fieldId]: {
              ...(prev[fieldId] || {}),
              [(prev[fieldId]?.length || 1) - 1]: false // Mark previous instance as not continuing
            }
          }));
        };

        const handleContinueResponse = (fieldId, instanceIndex, shouldContinue) => {
          setRepeatableContinueStates(prev => ({
            ...prev,
            [fieldId]: {
              ...(prev[fieldId] || {}),
              [instanceIndex]: shouldContinue
            }
          }));

          if (shouldContinue) {
            addRepeatableInstance(fieldId);
          }
        };

        const renderRepeatableField = (repeatableField, instanceIndex, fieldId) => {
          const fieldName = `${fieldId}_${instanceIndex}_${repeatableField.name}`;
          const fieldValue = formData[fieldName];

          switch (repeatableField.type) {
            case 'text':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldInput
                    type="text"
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={`Enter ${repeatableField.label.toLowerCase()}`}
                    style={{ 
                      backgroundColor: 'white',
                      width: '100%'
                    }}
                  />
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'textarea':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldTextarea
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={`Enter ${repeatableField.label.toLowerCase()}`}
                    rows={4}
                    style={{ 
                      backgroundColor: 'white',
                      width: '100%'
                    }}
                  />
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'heading':
              const headingStyle = {
                fontSize: repeatableField.level === 1 ? '28px' : repeatableField.level === 2 ? '24px' : repeatableField.level === 3 ? '20px' : repeatableField.level === 4 ? '18px' : repeatableField.level === 5 ? '16px' : '14px',
                fontWeight: '700',
                color: '#1f2937',
                margin: '16px 0 12px 0',
                padding: '0'
              };
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <div style={headingStyle}>{repeatableField.label || 'Heading'}</div>
                </div>
              );

            case 'subheading':
              const subheadingStyle = {
                fontSize: repeatableField.level === 1 ? '20px' : repeatableField.level === 2 ? '18px' : repeatableField.level === 3 ? '16px' : repeatableField.level === 4 ? '14px' : repeatableField.level === 5 ? '12px' : '14px',
                fontWeight: '600',
                color: '#374151',
                margin: '12px 0 8px 0',
                padding: '0'
              };
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <div style={subheadingStyle}>{repeatableField.label || 'Subheading'}</div>
                </div>
              );

            case 'rating':
              const rating = fieldValue || 0;
              const maxRating = repeatableField.maxRating || 5;
              const ratingLabels = repeatableField.ratingLabels || [];
              const ratingIcon = repeatableField.ratingIcon || 'star';
              // Use Unicode characters that respond to CSS color where possible
              const iconMap = { 
                star: { filled: '★', empty: '☆' },
                heart: { filled: '❤', empty: '♡' },
                thumbs: { filled: '👍', empty: '👍' }, // Emoji - will use filter
                face: { filled: '😊', empty: '😊' }, // Emoji - will use filter
                fire: { filled: '🔥', empty: '🔥' }, // Emoji - will use filter
                check: { filled: '✔', empty: '☐' }
              };
              const emotionFaces = ['😢', '😐', '😊', '😄', '😍'];
              
              // Check if icon is an emoji that needs filter (doesn't respond to CSS color)
              const isEmojiIcon = (icon) => {
                return ['thumbs', 'face', 'fire'].includes(icon) || icon === 'emotion';
              };
              
              // Get icon per index, taking into account filled state
              const getIconForIndex = (ratingValue, isFilled) => {
                if (ratingIcon === 'emotion') {
                  const faceIndex = Math.min(Math.max(ratingValue - 1, 0), emotionFaces.length - 1);
                  return emotionFaces[faceIndex];
                }
                const iconSet = iconMap[ratingIcon] || iconMap.star;
                return isFilled ? iconSet.filled : iconSet.empty;
              };
              
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {Array.from({ length: maxRating }, (_, index) => {
                      const isFilled = index < rating;
                      const iconNeedsFilter = isEmojiIcon(ratingIcon) || (ratingIcon === 'emotion');
                      return (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleInputChange(fieldName, index + 1)}
                        style={{
                          fontSize: '24px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                            color: isFilled ? '#fbbf24' : '#d1d5db',
                            transition: 'color 0.2s ease, filter 0.2s ease',
                            transform: isFilled ? 'scale(1.1)' : 'scale(1)',
                            // For emoji icons, use filter to make them grey when not filled
                            ...(iconNeedsFilter && !isFilled ? {
                              filter: 'grayscale(100%) opacity(0.4)',
                            } : {}),
                            ...(iconNeedsFilter && isFilled ? {
                              filter: 'none',
                            } : {})
                        }}
                      >
                          {getIconForIndex(index + 1, isFilled)}
                      </button>
                      );
                    })}
                  </div>
                  {rating > 0 && (
                    <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                      Selected: {rating}
                      {ratingLabels[rating - 1] && ` (${ratingLabels[rating - 1]})`}
                    </div>
                  )}
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'number':
            case 'email':
            case 'date':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldInput
                    type={repeatableField.type}
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    placeholder={repeatableField.placeholder || `Enter ${repeatableField.label.toLowerCase()}`}
                    style={{ 
                      backgroundColor: 'white',
                      width: '100%'
                    }}
                  />
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'checkbox':
              if (isSingleAgreementCheckbox(repeatableField)) {
                return (
                  <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                    <AgreementCheckboxField
                      field={repeatableField}
                      checked={!!fieldValue}
                      onChange={(value) => handleInputChange(fieldName, value)}
                      error={errors[fieldName]}
                    />
                  </div>
                );
              }
              const checkboxValues = fieldValue || [];
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <CheckboxGroup
                    fontFamily={repeatableField.fontFamily}
                    bold={repeatableField.bold}
                    italic={repeatableField.italic}
                    inputPaddingLeft={repeatableField.inputPaddingLeft}
                    checkboxLayout={repeatableField.checkboxLayout || 'vertical'}
                  >
                    {repeatableField.options?.map((option, index) => (
                      <CheckboxItem key={index}>
                        <CheckboxInput
                          type="checkbox"
                          value={option}
                          checked={checkboxValues.includes(option)}
                          onChange={(e) => {
                            const currentValues = checkboxValues;
                            if (e.target.checked) {
                              handleInputChange(fieldName, [...currentValues, option]);
                            } else {
                              handleInputChange(
                                fieldName,
                                currentValues.filter(val => val !== option)
                              );
                            }
                          }}
                        />
                        <span>{option}</span>
                      </CheckboxItem>
                    ))}
                  </CheckboxGroup>
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'select':
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FieldSelect
                    value={fieldValue || ''}
                    onChange={(e) => handleInputChange(fieldName, e.target.value)}
                    style={{ 
                      backgroundColor: 'white',
                      width: '100%'
                    }}
                  >
                    <option value="">Select {repeatableField.label.toLowerCase()}</option>
                    {repeatableField.options?.map((option, index) => (
                      <option key={index} value={option}>
                        {getSelectOptionDisplayText(repeatableField, option)}
                      </option>
                    ))}
                  </FieldSelect>
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'file':
              const repeatableFiles = uploadedFiles[fieldName] || [];
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <FileUploadContainer
                    onClick={() => document.getElementById(`file-${fieldName}`).click()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('drag-over');
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('drag-over');
                      const droppedFiles = e.dataTransfer.files;
                      handleRepeatableFileUpload(fieldName, droppedFiles, repeatableField);
                    }}
                  >
                    <FileUploadInput
                      id={`file-${fieldName}`}
                      type="file"
                      multiple={repeatableField.multipleFiles}
                      accept={formatAcceptAttribute(repeatableField.acceptedFileTypes)}
                      onChange={(e) => handleRepeatableFileUpload(fieldName, e.target.files, repeatableField)}
                    />
                    <FileUploadText>
                      <FileUploadIcon>
                        <FiUpload />
                      </FileUploadIcon>
                      <div>
                        <strong>Click to upload</strong> or drag and drop
                      </div>
                      <div style={{ fontSize: '14px' }}>
                        {repeatableField.acceptedFileTypes && repeatableField.acceptedFileTypes.length > 0 && (
                          <>Accepted types: {repeatableField.acceptedFileTypes.join(', ')}</>
                        )}
                        {repeatableField.maxFileSize && (
                          <> • Max size: {formatFileSize(repeatableField.maxFileSize * 1024 * 1024)}</>
                        )}
                      </div>
                    </FileUploadText>
                  </FileUploadContainer>
                  
                  {repeatableFiles.length > 0 && (
                    <FileList>
                      {repeatableFiles.map((file, index) => (
                        <FileItem key={index}>
                          <FileInfo>
                            <FiUpload />
                            <div>
                              <div>{file.name}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {formatFileSize(file.size)}
                              </div>
                            </div>
                          </FileInfo>
                          <RemoveFileButton type="button" onClick={() => removeFile(fieldName, index)}>
                            Remove
                          </RemoveFileButton>
                        </FileItem>
                      ))}
                    </FileList>
                  )}
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );

            case 'mapLocation': {
              const mapVal = fieldValue && typeof fieldValue === 'object' && typeof fieldValue.lat === 'number' && typeof fieldValue.lng === 'number'
                && !Number.isNaN(fieldValue.lat) && !Number.isNaN(fieldValue.lng)
                ? fieldValue
                : null;
              return (
                <div key={repeatableField.id} style={{ marginBottom: '16px' }}>
                  <FieldLabel required={repeatableField.required}>
                    {repeatableField.label}
                  </FieldLabel>
                  <MapLocationField
                    value={mapVal}
                    onChange={(coords) => handleInputChange(fieldName, coords)}
                  />
                  {errors[fieldName] && (
                    <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                      {errors[fieldName]}
                    </div>
                  )}
                </div>
              );
            }

            default:
              return null;
          }
        };

        return (
          <>
            {instances.map((instanceIndex) => {
              const instanceKey = `${field.id}_instance_${instanceIndex}`;
              const showContinue = continueStates[instanceIndex] === undefined && instanceIndex < instances.length - 1;
              const canContinue = instanceIndex === instances.length - 1 && continueStates[instanceIndex] === undefined;

              return (
                <React.Fragment key={instanceKey}>
                  {instances.length > 1 && instanceIndex > 0 && (
                    <div style={{
                      marginTop: '32px',
                      marginBottom: '24px',
                      height: '1px',
                      backgroundColor: '#e5e7eb'
                    }} />
                  )}
                  
                  {instances.length > 1 && (
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      marginBottom: '16px',
                      marginTop: instanceIndex > 0 ? '0' : '0'
                    }}>
                      {field.label || 'Repeatable Group'} #{instanceIndex + 1}
                    </div>
                  )}

                  {field.repeatableFields?.map((repeatableField) =>
                    renderRepeatableField(repeatableField, instanceIndex, field.id)
                  )}

                  {canContinue && field.showContinueButton !== false && (
                    <div style={{
                      marginTop: '24px',
                      marginBottom: '32px',
                      padding: '16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '500',
                        color: '#374151',
                        marginBottom: '12px'
                      }}>
                        {field.continueQuestion || 'Do you want to add another?'}
                      </div>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <button
                          type="button"
                          onClick={() => handleContinueResponse(field.id, instanceIndex, true)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                        >
                          {field.continueYesLabel || 'Yes, add another'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleContinueResponse(field.id, instanceIndex, false)}
                          style={{
                            padding: '10px 20px',
                            backgroundColor: '#6b7280',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#4b5563'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#6b7280'}
                        >
                          {field.continueNoLabel || 'No, submit form'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Add "Add Another" button when continue button is hidden */}
                  {canContinue && field.showContinueButton === false && (
                    <div style={{
                      marginTop: '24px',
                      marginBottom: '16px'
                    }}>
                      <button
                        type="button"
                        onClick={() => handleContinueResponse(field.id, instanceIndex, true)}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                      >
                        {field.continueYesLabel || 'Add Another'}
                      </button>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </>
        );

      default:
        console.log('FormView: Field falling to default case:', field);
        return (
          <FieldInput
            type="text"
            {...fieldProps}
            placeholder={field.placeholder}
          />
        );
    }
  };

  // OLD RENDER LOGIC REMOVED - Using password-aware render logic below (starting at line 2863)

  if (submitted) {
    return (
      <FormContainer>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
        <FormCard>
          <SuccessMessage>
            <SuccessIcon>
              <FiCheck />
            </SuccessIcon>
            <h2>Thank you!</h2>
            <p>Your response has been submitted successfully.</p>

            {isQuestionnaire(form) && form.form_structure?.enableTimer && scoreData && (
              <>
                <ScoreCard>
                  <ScoreTitle><FiAward /> Your Score</ScoreTitle>
                  <ScoreValue>{scoreData.earnedPoints} / {scoreData.totalPoints}</ScoreValue>
                  <ScoreDetails>{scoreData.percentage.toFixed(1)}% &middot; {scoreData.remark}</ScoreDetails>
                  <ScoreGrade>Grade: {scoreData.grade}</ScoreGrade>
                  {scoreData.hasManualGrading && (
                    <ScoreDetails style={{ marginTop: '12px', fontSize: '14px' }}>
                      Some answers require manual review — your final score may change.
                    </ScoreDetails>
                  )}
                </ScoreCard>

                <QuestionResults>
                  {scoreData.questionResults.map((q) => (
                    <QuestionResult
                      key={q.questionNumber}
                      isCorrect={q.isCorrect}
                      requiresGrading={q.requiresManualGrading}
                    >
                      <QuestionResultHeader>
                        <QuestionText>{q.questionNumber}. {q.questionText}</QuestionText>
                        <QuestionScore isCorrect={q.isCorrect} requiresGrading={q.requiresManualGrading}>
                          {q.requiresManualGrading ? (
                            'Pending Review'
                          ) : (
                            <>
                              {q.isCorrect ? <FiCheckCircle /> : <FiXCircle />}
                              {q.earnedPoints}/{q.points}
                            </>
                          )}
                        </QuestionScore>
                      </QuestionResultHeader>
                      <AnswerDetails>
                        <div>Your answer: {Array.isArray(q.userAnswer) ? q.userAnswer.join(', ') : (q.userAnswer || '(No answer)')}</div>
                        {!q.isCorrect && !q.requiresManualGrading && q.correctAnswer && (
                          <div>Correct answer: {q.correctAnswer}</div>
                        )}
                      </AnswerDetails>
                    </QuestionResult>
                  ))}
                </QuestionResults>
              </>
            )}

            {isQuestionnaire(form) && form.enable_certificate && (
              <div
                style={{
                  marginTop: '24px',
                  padding: '20px',
                  background: '#eff6ff',
                  borderRadius: '12px',
                  border: '1px solid #dbeafe'
                }}
              >
                <div
                  style={{
                    fontSize: '16px',
                    color: '#1e40af',
                    fontWeight: '600',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <FiAward style={{ color: '#1d4ed8' }} />
                  Certificate Center
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#1e3a8a',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}
                >
                  {certificateGenerating
                    ? 'Generating your certificate... this usually takes just a few seconds.'
                    : certificateData
                      ? 'Thank you for completing the assessment! Use the options below to view or download your certificate.'
                      : certificateError
                        ? certificateError
                        : 'Thank you for completing the assessment. Your certificate will appear below momentarily.'}
                </div>

                {certificateData && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '16px',
                      background: '#f0fdf4',
                      borderRadius: '8px',
                      border: '1px solid #bbf7d0'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '15px',
                        color: '#166534',
                        marginBottom: '12px'
                      }}
                    >
                      {`Ready for ${certificateData.candidate_name}!`}
                    </div>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          if (!certificateData?.html) {
                            toast.error('Certificate HTML is not available.');
                            return;
                          }

                          const certificateWindow = window.open('', '_blank');
                          if (certificateWindow) {
                            certificateWindow.document.write(certificateData.html);
                            certificateWindow.document.close();
                            
                            // Track the view (fire and forget)
                            if (certificateData.certificate_id) {
                              certificatesAPI.trackDownload(certificateData.certificate_id, 'html')
                                .then(() => {
                                  console.log('✅ Certificate view tracked successfully');
                                })
                                .catch((trackError) => {
                                  console.error('⚠️ Failed to track certificate view:', trackError);
                                });
                            }
                          } else {
                            toast.error('Please allow popups to view the certificate.');
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          background: '#16a34a',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease'
                        }}
                        onMouseEnter={(e) => (e.target.style.background = '#15803d')}
                        onMouseLeave={(e) => (e.target.style.background = '#16a34a')}
                      >
                        <FiAward />
                        View Certificate
                      </button>
                      <button
                        onClick={async () => {
                          if (downloadingPdf) return;
                          
                          try {
                            if (!certificateData?.certificate_id) {
                              throw new Error('Missing certificate identifier.');
                            }

                            setDownloadingPdf(true);
                            toast.info('Generating PDF... Please wait.', { autoClose: 2000 });

                            const response = await certificatesAPI.getCertificatePDF(
                              certificateData.certificate_id
                            );
                            
                            // Check if response is actually a PDF blob
                            if (!response.data) {
                              throw new Error('No data received from server');
                            }
                            
                            // Check if response is an error (JSON response instead of blob)
                            // When axios gets an error with blob responseType, it may return JSON as blob
                            if (response.data instanceof Blob) {
                              // Check if it's actually JSON (error response)
                              if (response.data.type === 'application/json' || response.data.size < 100) {
                                const errorText = await response.data.text();
                                try {
                                  const errorData = JSON.parse(errorText);
                                  throw new Error(errorData.error || 'Failed to generate certificate PDF');
                                } catch (parseError) {
                                  // If it's not valid JSON, check if it's a small blob (likely an error)
                                  if (response.data.size < 100) {
                                    throw new Error('Failed to generate certificate PDF. Please try again.');
                                  }
                                  throw new Error('Failed to generate certificate PDF');
                                }
                              }
                              
                              // Verify it's a PDF by checking the first bytes
                              const firstBytes = await response.data.slice(0, 4).arrayBuffer();
                              const pdfHeader = new Uint8Array(firstBytes);
                              const isPdf = pdfHeader[0] === 0x25 && pdfHeader[1] === 0x50 && pdfHeader[2] === 0x44 && pdfHeader[3] === 0x46; // %PDF
                              
                              if (!isPdf) {
                                // Try to read as text to see if it's an error message
                                const text = await response.data.text();
                                if (text.includes('error') || text.includes('Error') || text.includes('failed')) {
                                  try {
                                    const errorData = JSON.parse(text);
                                    throw new Error(errorData.error || 'Failed to generate certificate PDF');
                                  } catch {
                                    throw new Error('Failed to generate certificate PDF. Server returned an error.');
                                  }
                                }
                                throw new Error('Invalid PDF file received');
                              }
                            }
                            
                            const blob = new Blob([response.data], { type: 'application/pdf' });
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `Certificate_${certificateData.candidate_name.replace(/\s+/g, '_')}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            window.URL.revokeObjectURL(url);
                            
                            // Note: Download tracking is already handled by the backend PDF endpoint
                            // No need to track separately
                            
                            toast.success('Certificate PDF downloaded!');
                          } catch (error) {
                            console.error('Download error:', error);
                            console.error('Error details:', {
                              message: error.message,
                              response: error.response,
                              status: error.response?.status,
                              data: error.response?.data
                            });
                            
                            // Handle axios errors (network errors, 500 errors, etc.)
                            let errorMessage = 'Failed to download certificate.';
                            
                            if (error.response) {
                              // Server responded with error status
                              const status = error.response.status;
                              
                              if (error.response.data instanceof Blob) {
                                // Error response is a blob, try to parse it
                                try {
                                  const errorText = await error.response.data.text();
                                  console.log('Error response text:', errorText);
                                  const errorData = JSON.parse(errorText);
                                  errorMessage = errorData.error || `Server error (${status}): Failed to generate certificate PDF`;
                                } catch (parseError) {
                                  console.error('Failed to parse error response:', parseError);
                                  errorMessage = `Server error (${status}): Failed to generate certificate PDF. Please try again later.`;
                                }
                              } else if (error.response.data) {
                                // Check if it's already parsed JSON
                                if (typeof error.response.data === 'object' && error.response.data.error) {
                                  errorMessage = error.response.data.error;
                                } else if (typeof error.response.data === 'string') {
                                  try {
                                    const errorData = JSON.parse(error.response.data);
                                    errorMessage = errorData.error || `Server error (${status})`;
                                  } catch {
                                    errorMessage = `Server error (${status}): ${error.response.data}`;
                                  }
                                } else {
                                  errorMessage = `Server error (${status}): Failed to generate certificate PDF`;
                                }
                              } else {
                                errorMessage = `Server error (${status}): Failed to generate certificate PDF`;
                              }
                            } else if (error.request) {
                              // Request was made but no response received
                              errorMessage = 'Network error. Please check your connection and try again.';
                            } else {
                              // Error in request setup
                              errorMessage = error.message || 'Failed to download certificate.';
                            }
                            
                            console.error('Final error message:', errorMessage);
                            toast.error(errorMessage);
                          } finally {
                            setDownloadingPdf(false);
                          }
                        }}
                        disabled={downloadingPdf}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '8px 16px',
                          background: downloadingPdf ? '#9ca3af' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          fontWeight: '500',
                          cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                          transition: 'background-color 0.2s ease',
                          opacity: downloadingPdf ? 0.7 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!downloadingPdf) {
                            e.target.style.background = '#2563eb';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!downloadingPdf) {
                            e.target.style.background = '#3b82f6';
                          }
                        }}
                      >
                        {downloadingPdf ? (
                          <>
                            <div style={{
                              width: '14px',
                              height: '14px',
                              border: '2px solid white',
                              borderTopColor: 'transparent',
                              borderRadius: '50%',
                              animation: 'spin 0.8s linear infinite',
                              display: 'inline-block'
                            }} />
                            Generating...
                          </>
                        ) : (
                          <>
                            <FiCheck />
                            Download Certificate
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {!certificateGenerating && !certificateData && certificateError && (
                  <div
                    style={{
                      marginTop: '16px',
                      padding: '16px',
                      background: '#fef2f2',
                      borderRadius: '8px',
                      border: '1px solid #fecaca',
                      color: '#b91c1c',
                      fontSize: '14px'
                    }}
                  >
                    If the issue persists, please contact the form administrator for assistance.
                  </div>
                )}
              </div>
            )}
          </SuccessMessage>
        </FormCard>
      </FormContainer>
    );
  }

  const handleBackClick = () => {
    // Check if this form was opened in a new tab (from dashboard)
    if (window.opener) {
      // Close the tab since it was opened from dashboard
      window.close();
    } else {
      // Navigate back for regular navigation
      navigate(-1);
    }
  };


  // Don't show form if loading
  if (loading) {
    return (
      <FormContainer>
        <LoadingSpinner>Loading form...</LoadingSpinner>
      </FormContainer>
    );
  }

  // Don't show form if form data is not loaded
  if (!form) {
    console.log('FormView: Form not loaded yet, showing loading spinner. loading:', loading);
    return (
      <FormContainer>
        <LoadingSpinner>Loading form...</LoadingSpinner>
      </FormContainer>
    );
  }
  
  // Debug: Log when form should be displayed
  console.log('FormView: Rendering form. form loaded:', !!form);

  return (
    <FormContainer>
      {/* Only show back button if user is authenticated (logged in) */}
      {user && (
        <BackButton onClick={handleBackClick}>
          <FiArrowLeft />
          {window.opener ? 'Close' : 'Back'}
        </BackButton>
      )}

      <FormCard>
        {isQuestionnaire(form) && form.form_structure?.enableTimer && timeRemainingSeconds !== null && (
          <TimerBanner $urgent={timeRemainingSeconds <= 60}>
            <FiClock />
            Time Remaining: {formatTimerDisplay(timeRemainingSeconds)}
          </TimerBanner>
        )}

        {/* Show logo and description only on first step or non-multi-step forms */}
        {(!isMultiStep || currentStep === 0) && (
          <>
            <LogoContainer>
              {form.logo_url ? (
                <img 
                  src={form.logo_url} 
                  alt="Company Logo" 
                  onError={(e) => {
                    console.error('Failed to load logo:', form.logo_url);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => console.log('Logo loaded successfully:', form.logo_url)}
                />
              ) : (
                <DefaultLogo>
                  {form.title.charAt(0).toUpperCase()}
                </DefaultLogo>
              )}
            </LogoContainer>
            
            <FormTitle>{form.title}</FormTitle>
            {form.description && (
              <FormDescription>{form.description}</FormDescription>
            )}
          </>
        )}

        {/* Show title only (without logo/description) on subsequent steps */}
        {isMultiStep && currentStep > 0 && (
          <FormTitle style={{ marginTop: '0', marginBottom: '24px' }}>
            {form.title}
          </FormTitle>
        )}

        <form onSubmit={handleSubmit}>
          {/* Render questionnaire questions if this is a questionnaire */}
          {isQuestionnaire(form) ? (
            <div>
              {/* Render form fields first if they exist - hide in multi-step if not on intro step */}
              {form.form_structure.formFields && form.form_structure.formFields.length > 0 && (!isMultiStep || showIntroOnly) && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#1f2937', 
                    marginBottom: '20px',
                    paddingBottom: '12px',
                    borderBottom: '2px solid #e5e7eb'
                  }}>
                    Your Information
                  </h3>
                  
                  {/* Render fields in rows based on width */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {(() => {
                      const rows = [];
                      let currentRow = [];
                      let currentRowWidth = 0;
                      // Use the state instead of checking window.innerWidth directly

                      form.form_structure.formFields.forEach((field, index) => {
                        // On mobile, force all fields to be full width
                        const fieldWidth = isMobile ? 100 : (field.width === 'half' ? 50 : field.width === 'third' ? 33 : 100);
                        
                        // If adding this field would exceed 100%, start a new row
                        if (currentRowWidth + fieldWidth > 100 && currentRow.length > 0) {
                          rows.push([...currentRow]);
                          currentRow = [];
                          currentRowWidth = 0;
                        }
                        
                        currentRow.push(field);
                        currentRowWidth += fieldWidth;
                        
                        // If row is full or it's a full-width field, complete the row
                        if (currentRowWidth >= 100) {
                          rows.push([...currentRow]);
                          currentRow = [];
                          currentRowWidth = 0;
                        }
                      });
                      
                      // Add remaining fields
                      if (currentRow.length > 0) {
                        rows.push(currentRow);
                      }

                      return rows.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ 
                          display: 'grid', 
                          gridTemplateColumns: isMobile ? '1fr' : row.map(f => {
                            if (f.width === 'half') return '1fr';
                            if (f.width === 'third') return '1fr';
                            return '1fr';
                          }).join(' '),
                          gap: isMobile ? '12px' : '16px'
                        }}>
                          {row.map((field) => {
                            const fieldKey = field.name || field.id;
                            const currentValue = formData[fieldKey];

                            return (
                              <FormField key={fieldKey}>
                                <FieldLabel 
                                  required={field.required} 
                                  fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                                  fontFamily={field.fontFamily}
                                  bold={field.bold}
                                  italic={field.italic}
                                >
                                  {field.label || field.question}
                                </FieldLabel>
                                <FieldInput
                                  type={field.type}
                                  value={currentValue || ''}
                                  onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                                  placeholder={field.label}
                                  required={field.required}
                                />
                                {errors[fieldKey] && (
                                  <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                                    {errors[fieldKey]}
                                  </div>
                                )}
                              </FormField>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                  
                  <div style={{ 
                    height: '1px', 
                    background: '#e5e7eb', 
                    margin: '32px 0',
                  }} />
                </div>
              )}

              {/* Render questions - hide in multi-step if on intro step */}
              {(!isMultiStep || !showIntroOnly) && (
                <div>
                  <h3 style={{ 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#1f2937', 
                    marginBottom: '20px'
                  }}>
                    Answer these questions
                  </h3>
                  {allFields && allFields.length > 0 ? (
                  visibleFields.map((question, visibleIndex) => {
                const actualIndex = isMultiStep ? questionIndex : allFields.indexOf(question);
                const questionKey = question.id;
                const currentValue = formData[questionKey];
                
                return (
                  <QuestionCard key={questionKey}>
                    <QuestionNumber>
                      Question {actualIndex + 1} of {filteredFields.length}
                    </QuestionNumber>
                    
                    <QuestionTitle>
                      {question.label || question.question}
                      {question.required && <span style={{ color: '#ef4444' }}> *</span>}
                    </QuestionTitle>

                    {/* Multiple Choice */}
                    {(question.type === 'multiple_choice' || question.type === 'true_false') && (
                      <QuestionOptions>
                        {question.options?.map((option) => (
                          <OptionItem 
                            key={option.id}
                            checked={currentValue === option.text}
                          >
                            <OptionInput
                              type="radio"
                              name={questionKey}
                              value={option.text}
                              checked={currentValue === option.text}
                              onChange={(e) => handleInputChange(questionKey, e.target.value)}
                              required={question.required}
                            />
                            <OptionText checked={currentValue === option.text}>
                              {option.text}
                            </OptionText>
                          </OptionItem>
                        ))}
                      </QuestionOptions>
                    )}

                    {/* Checkbox (Multiple Select) */}
                    {question.type === 'checkbox' && (
                      <QuestionOptions>
                        {question.options?.map((option) => {
                          const selectedValues = Array.isArray(currentValue) ? currentValue : [];
                          const isChecked = selectedValues.includes(option.text);
                          
                          return (
                            <OptionItem 
                              key={option.id}
                              checked={isChecked}
                            >
                              <OptionInput
                                type="checkbox"
                                value={option.text}
                                checked={isChecked}
                                onChange={(e) => {
                                  const newValues = isChecked
                                    ? selectedValues.filter(v => v !== option.text)
                                    : [...selectedValues, option.text];
                                  handleInputChange(questionKey, newValues);
                                }}
                              />
                              <OptionText checked={isChecked}>
                                {option.text}
                              </OptionText>
                            </OptionItem>
                          );
                        })}
                      </QuestionOptions>
                    )}

                    {/* Short Answer */}
                    {question.type === 'short_answer' && (
                      <FieldTextarea
                        value={currentValue || ''}
                        onChange={(e) => handleInputChange(questionKey, e.target.value)}
                        placeholder="Enter your answer here..."
                        required={question.required}
                        style={{ marginTop: '12px' }}
                      />
                    )}

                    {errors[questionKey] && (
                      <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>
                        {errors[questionKey]}
                      </div>
                    )}
                  </QuestionCard>
                );
              })
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                    <p>This questionnaire has no questions configured yet.</p>
                  </div>
                )}
                </div>
              )}
            </div>
          ) : form.form_structure && Array.isArray(form.form_structure) ? (() => {
            // For multi-step forms with section grouping, show the current group
            if (isMultiStep && showQuestionsOnly && groupSectionsWithQuestions) {
              const stepIndex = hasIntroSection ? currentStep - 1 : currentStep;
              const currentGroup = stepGroups[stepIndex];
              if (!currentGroup) return null;
              
              return (
                <div>
                  {/* Show section header if it exists */}
                  {currentGroup.section && (
                    <div style={{ margin: '24px 0 12px 0' }}>
                      {(() => {
                        const section = currentGroup.section;
                        const sectionHeadingStyle = {
                          fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                          fontWeight: section.bold ? '700' : '600',
                          fontStyle: section.italic ? 'italic' : 'normal',
                          textDecoration: section.underline ? 'underline' : 'none',
                          fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                          color: section.textColor || '#1f2937',
                          textAlign: section.alignment || 'left',
                          margin: '0 0 8px 0',
                          padding: '0'
                        };
                        
                        console.log('FormView: Rendering grouped section field:', section.label);
                        console.log('FormView: Grouped section field data:', {
                          bold: section.bold,
                          alignment: section.alignment,
                          fontFamily: section.fontFamily,
                          textColor: section.textColor,
                          level: section.level,
                          italic: section.italic
                        });
                        console.log('FormView: Grouped section heading style:', sectionHeadingStyle);
                        
                        return (
                          <div style={sectionHeadingStyle}>{section.label}</div>
                        );
                      })()}
                      {currentGroup.section.sectionSubtitle && (
                        <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: currentGroup.section.sectionDivider ? '12px' : '0' }}>
                          {currentGroup.section.sectionSubtitle}
                        </div>
                      )}
                      {currentGroup.section.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                    </div>
                  )}
                  
                  {/* Render all fields in this group */}
                  <FieldsGrid>
                  {currentGroup.fields.map(field => {
                    // Special rendering for heading fields
                    if (field.type === 'heading') {
                      const headingStyle = {
                        fontSize: field.level === 1 ? '28px' : field.level === 2 ? '24px' : field.level === 3 ? '20px' : field.level === 4 ? '18px' : field.level === 5 ? '16px' : '14px',
                        fontWeight: field.bold ? '700' : '600',
                        fontStyle: field.italic ? 'italic' : 'normal',
                        textDecoration: field.underline ? 'underline' : 'none',
                        fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                        color: field.textColor || '#1f2937',
                        textAlign: field.alignment || 'left',
                        margin: '24px 0 12px 0',
                          padding: '0',
                          gridColumn: 'span 12' // Always full width
                      };
                      return (
                        <div key={field.id} style={headingStyle}>
                          {field.label}
                        </div>
                      );
                    }
                    
                    // Special rendering for subheading fields
                    if (field.type === 'subheading') {
                      const subheadingStyle = {
                        fontSize: field.level === 1 ? '22px' : field.level === 2 ? '20px' : field.level === 3 ? '18px' : field.level === 4 ? '16px' : field.level === 5 ? '15px' : '14px',
                        fontWeight: field.bold ? '600' : '500',
                        fontStyle: field.italic ? 'italic' : 'normal',
                        textDecoration: field.underline ? 'underline' : 'none',
                        fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                        color: field.textColor || '#4b5563',
                        textAlign: field.alignment || 'left',
                        margin: '16px 0 8px 0',
                          padding: '0',
                          gridColumn: 'span 12' // Always full width
                      };
                      return (
                        <div key={field.id} style={subheadingStyle}>
                          {field.label || field.question}
                        </div>
                      );
                    }
                    
                    return (
                      <FormField 
                        key={field.id}
                          fieldWidth={field.fieldWidth || 'full'}
                          fieldType={field.type}
                      >
                        {!isSingleAgreementCheckbox(field) && (
                          <FieldLabel 
                            required={field.required} 
                            fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                            fontFamily={field.fontFamily}
                            bold={field.bold}
                            italic={field.italic}
                          >
                            {field.label || field.question}
                          </FieldLabel>
                        )}
                        {renderField(field)}
                        {errors[field.name] && (
                          <ErrorMessage>{errors[field.name]}</ErrorMessage>
                        )}
                      </FormField>
                    );
                  })}
                  </FieldsGrid>
                </div>
              );
            }
            
            // For multi-step forms without section grouping, show single field with section header
            if (isMultiStep && showQuestionsOnly && !groupSectionsWithQuestions) {
              const currentField = filteredFields[questionIndex];
              if (!currentField) return null;
              
              // Find any section headers that should appear before this field
              const allFieldsWithSections = allFields.filter(field => field.visible !== false);
              const currentFieldIndex = allFieldsWithSections.findIndex(f => f.id === currentField.id);
              const sectionsBeforeCurrent = allFieldsWithSections.slice(0, currentFieldIndex).filter(f => f.type === 'section');
              const lastSectionBeforeCurrent = sectionsBeforeCurrent[sectionsBeforeCurrent.length - 1];
              
              return (
                <div>
                  {/* Show the last section header before current field if it exists */}
                  {lastSectionBeforeCurrent && (
                    <div style={{ margin: '24px 0 12px 0' }}>
                      {(() => {
                        const section = lastSectionBeforeCurrent;
                        const sectionHeadingStyle = {
                          fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                          fontWeight: section.bold ? '700' : '600',
                          fontStyle: section.italic ? 'italic' : 'normal',
                          textDecoration: section.underline ? 'underline' : 'none',
                          fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                          color: section.textColor || '#1f2937',
                          textAlign: section.alignment || 'left',
                          margin: '0 0 8px 0',
                          padding: '0'
                        };
                        
                        console.log('FormView: Rendering last section field:', section.label);
                        console.log('FormView: Last section field data:', {
                          bold: section.bold,
                          alignment: section.alignment,
                          fontFamily: section.fontFamily,
                          textColor: section.textColor,
                          level: section.level,
                          italic: section.italic
                        });
                        console.log('FormView: Last section heading style:', sectionHeadingStyle);
                        
                        return (
                          <div style={sectionHeadingStyle}>{section.label}</div>
                        );
                      })()}
                      {lastSectionBeforeCurrent.sectionSubtitle && (
                        <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: lastSectionBeforeCurrent.sectionDivider ? '12px' : '0' }}>
                          {lastSectionBeforeCurrent.sectionSubtitle}
                        </div>
                      )}
                      {lastSectionBeforeCurrent.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                    </div>
                  )}
                  {/* Render any subheadings between the last section and current field */}
                  {(() => {
                    const startIndex = lastSectionBeforeCurrent
                      ? allFieldsWithSections.findIndex(f => f.id === lastSectionBeforeCurrent.id) + 1
                      : 0;
                    const subheadingsBetween = allFieldsWithSections
                      .slice(startIndex, currentFieldIndex)
                      .filter(f => f.type === 'subheading');

                    return subheadingsBetween.map(sh => {
                      const subheadingStyle = {
                        fontSize: sh.level === 1 ? '22px' : sh.level === 2 ? '20px' : sh.level === 3 ? '18px' : sh.level === 4 ? '16px' : sh.level === 5 ? '15px' : '14px',
                        fontWeight: sh.bold ? '600' : '500',
                        fontStyle: sh.italic ? 'italic' : 'normal',
                        textDecoration: sh.underline ? 'underline' : 'none',
                        fontFamily: sh.fontFamily && sh.fontFamily !== 'default' ? sh.fontFamily : 'inherit',
                        color: sh.textColor || '#4b5563',
                        textAlign: sh.alignment || 'left',
                        margin: '16px 0 8px 0',
                        padding: '0'
                      };
                      return (
                        <div key={sh.id} style={subheadingStyle}>
                          {sh.label || sh.question}
                        </div>
                      );
                    });
                  })()}

                  {/* Render the current field with its label */}
                  <FormField key={currentField.id}>
                    {!isSingleAgreementCheckbox(currentField) && (
                      <FieldLabel 
                        required={currentField.required} 
                        fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                        fontFamily={currentField.fontFamily}
                        bold={currentField.bold}
                        italic={currentField.italic}
                      >
                        {currentField.label || currentField.question}
                      </FieldLabel>
                    )}
                    {renderField(currentField)}
                    {errors[currentField.name] && (
                      <ErrorMessage>{errors[currentField.name]}</ErrorMessage>
                    )}
                  </FormField>
                </div>
              );
            }
            
            // For non-multi-step forms, use the original grouping logic
            const groups = [];
            let current = { section: null, fields: [] };
            const items = (visibleFields || form.form_structure).filter(f => f && f.type !== 'heading');
            items.forEach((f) => {
              if (f.type === 'section') {
                if (current.section || current.fields.length > 0) groups.push(current);
                current = { section: f, fields: [] };
              } else {
                current.fields.push(f);
              }
            });
            if (current.section || current.fields.length > 0) groups.push(current);

            return groups.map((group, gi) => (
              <div key={group.section?.id || `group_${gi}`} style={{ 
                marginBottom: '24px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#ffffff',
                padding: '16px'
              }}>
                {group.section && (
                  <div style={{ margin: '24px 0 12px 0' }}>
                    {(() => {
                      const section = group.section;
                      const sectionHeadingStyle = {
                        fontSize: section.level === 1 ? '28px' : section.level === 2 ? '24px' : section.level === 3 ? '20px' : section.level === 4 ? '18px' : section.level === 5 ? '16px' : '14px',
                        fontWeight: section.bold ? '700' : '600',
                        fontStyle: section.italic ? 'italic' : 'normal',
                        textDecoration: section.underline ? 'underline' : 'none',
                        fontFamily: section.fontFamily && section.fontFamily !== 'default' ? section.fontFamily : 'inherit',
                        color: section.textColor || '#1f2937',
                        textAlign: section.alignment || 'left',
                        margin: '0 0 8px 0',
                        padding: '0'
                      };
                      
                      console.log('FormView: Rendering group section field:', section.label);
                      console.log('FormView: Group section field data:', {
                        bold: section.bold,
                        alignment: section.alignment,
                        fontFamily: section.fontFamily,
                        textColor: section.textColor,
                        level: section.level,
                        italic: section.italic
                      });
                      console.log('FormView: Group section heading style:', sectionHeadingStyle);
                      
                      return (
                        <div style={sectionHeadingStyle}>{section.label}</div>
                      );
                    })()}
                    {group.section.sectionSubtitle && (
                      <div style={{ color: '#6b7280', marginTop: '-8px', marginBottom: group.section.sectionDivider ? '12px' : '0' }}>
                        {group.section.sectionSubtitle}
                      </div>
                    )}
                    {group.section.sectionDivider && <div style={{ height: '1px', background: '#e5e7eb', marginTop: '12px' }} />}
                  </div>
                )}

                <FieldsGrid>
                {group.fields.map((field) => {
                  // Special rendering for subheading fields
                  if (field.type === 'subheading') {
                    const subheadingStyle = {
                      fontSize: field.level === 1 ? '22px' : field.level === 2 ? '20px' : field.level === 3 ? '18px' : field.level === 4 ? '16px' : field.level === 5 ? '15px' : '14px',
                      fontWeight: field.bold ? '600' : '500',
                      fontStyle: field.italic ? 'italic' : 'normal',
                      textDecoration: field.underline ? 'underline' : 'none',
                      fontFamily: field.fontFamily && field.fontFamily !== 'default' ? field.fontFamily : 'inherit',
                      color: field.textColor || '#4b5563',
                      textAlign: field.alignment || 'left',
                      margin: '16px 0 8px 0',
                        padding: '0',
                        gridColumn: 'span 12' // Always full width
                    };
                    return (
                      <div key={field.id} style={subheadingStyle}>
                        {field.label || field.question}
                      </div>
                    );
                  }
                  
                  if (field.type === 'contact') {
                    return (
                      <div key={field.id} style={{ 
                        border: '2px solid #e0f2fe', 
                        borderRadius: '8px', 
                        padding: '20px', 
                        marginBottom: '24px',
                        backgroundColor: '#f8fafc'
                      }}>
                        <ContactToggleButton 
                          type="button"
                          onClick={() => setContactBlockCollapsed(!contactBlockCollapsed)}
                        >
                          {contactBlockCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                          <span>📞</span>
                          Contact Information
                          <span style={{ fontSize: '14px', fontWeight: '400', color: '#6b7280' }}>
                            ({field.contactFields?.length || 0} fields)
                          </span>
                        </ContactToggleButton>
                        
                        <ContactFieldsContainer collapsed={contactBlockCollapsed}>
                          {field.contactFields?.map((contactField) => (
                            <FormField key={contactField.id} style={{ marginBottom: '16px' }}>
                              <FieldLabel required={contactField.required} fontSize={form?.labelFontSize || form?.label_font_size || '14px'}>
                                {contactField.label || contactField.question}
                              </FieldLabel>
                              {renderField(contactField)}
                              {errors[contactField.name] && (
                                <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                                  {errors[contactField.name]}
                                </div>
                              )}
                            </FormField>
                          ))}
                        </ContactFieldsContainer>
                      </div>
                    );
                  }

                  return (
                    <FormField 
                      key={field.id}
                      fieldWidth={field.fieldWidth || 'full'}
                      fieldType={field.type}
                    >
                      {!isSingleAgreementCheckbox(field) && (
                        <FieldLabel 
                          required={field.required} 
                          fontSize={form?.labelFontSize || form?.label_font_size || '14px'}
                          fontFamily={field.fontFamily}
                          bold={field.bold}
                          italic={field.italic}
                        >
                          {field.label}
                        </FieldLabel>
                      )}
                      {renderField(field)}
                      {errors[field.name] && (
                        <div style={{ color: '#ef4444', fontSize: '14px', marginTop: '4px' }}>
                          {errors[field.name]}
                        </div>
                      )}
                    </FormField>
                  );
                })}
                </FieldsGrid>
              </div>
            ));
          })() : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              <p>This form has no fields configured yet.</p>
            </div>
          )}

          {/* Multi-step navigation */}
          {isMultiStep && (
            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between', 
              alignItems: 'center',
              gap: isMobile ? '16px' : '0',
              marginTop: '32px',
              marginBottom: '24px',
              padding: isMobile ? '16px' : '20px',
              background: '#f9fafb',
              borderRadius: '12px',
              border: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: isMobile ? '14px 20px' : '12px 24px',
                  background: currentStep === 0 ? '#e5e7eb' : 'white',
                  color: currentStep === 0 ? '#9ca3af' : '#374151',
                  border: '2px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minHeight: isMobile ? '48px' : 'auto',
                  width: isMobile ? '100%' : 'auto',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (currentStep > 0) {
                    e.target.style.background = '#f3f4f6';
                    e.target.style.borderColor = '#9ca3af';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentStep > 0) {
                    e.target.style.background = 'white';
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
              >
                <FiArrowLeft />
                Previous
              </button>

              <div style={{ 
                fontSize: isMobile ? '13px' : '14px', 
                fontWeight: '600', 
                color: '#374151',
                textAlign: 'center',
                order: isMobile ? '-1' : '0'
              }}>
                {showIntroOnly ? 'Introduction' : `Question ${questionIndex + 1} of ${filteredFields.length}`}
              </div>

              {!isLastStep ? (
                <button
                  type="button"
                  onClick={handleNext}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: isMobile ? '14px 20px' : '12px 24px',
                    background: '#1f2937',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    minHeight: isMobile ? '48px' : 'auto',
                    width: isMobile ? '100%' : 'auto',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#374151';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#1f2937';
                  }}
                >
                  Next
                  <FiChevronRight />
                </button>
              ) : (
                !isMobile && <div style={{ width: '120px' }}></div>
              )}
            </div>
          )}

          {(!isMultiStep || isLastStep) && (
          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Submitting...' : (form?.submit_button_text && form.submit_button_text.trim()) ? form.submit_button_text.trim() : 'Submit Form'}
          </SubmitButton>
          )}
        </form>
      </FormCard>
    </FormContainer>
  );
}

export default FormView;
